// ManagerDashboard.tsx
import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import {
  deleteCompanyEmergencyContact,
  fetchAssessmentFlags,
  fetchCompanies,
  fetchCompanyEmergencyContact,
  fetchEmployeeCompanyIds,
  fetchEmployeesByCompanyId,
  fetchEmployeesByOccasion,
  fetchEventAssessmentFlags,
  fetchEventKontrolScores,
  fetchEventParticipantCounts,
  fetchEvents,
  fetchKontrolScoreFeedbackByEmployeeIds,
  fetchKontrolScoreFeedbackQuestions,
  fetchKontrolScoresByEmployeeIds,
  fetchManagerAvailabilityRows,
  fetchManagerImpactInputs,
  fetchManagerModuleRecommendations,
  fetchManagerViewerByAuthUserId,
  getCurrentUser,
  upsertCompanyEmergencyContact,
} from "../api/queries";
import { useI18n } from "../i18n/I18nProvider";
import type { AnswerImprovementRow, AnswerOptions, AssessmentFlagRow, AvailabilityRow, AvailabilitySummary, DepartmentSummary, ManagerRow, ExtPrsAnswerRow, ExtPrsQuestionRow, FlagSummary, IssueResolutionPotential, EmployeeScoreRow, ModuleImpactContext, ModuleImpactSummary, ModuleInsight, ModuleRecommendationRow, ModuleTaskCategoryRow, ModuleTaskProgressRow, PbgAnswerRow, PbgQuestionRow, ScoreBreakdown, ScoreSummary } from "../types/managerDashboard";
import type { CompanyEmergencyContact, CompanyRow } from "../types/company";
import type { KontrolScoreFeedbackQuestion, ScoreType } from "../types/scores";
import type { EventRow } from "../types/events";
import { labelForQuestion } from "./ScoreFeedback";
import { aggregateFeedbackQuestion, type FeedbackAggregate } from "./utils/feedbackAggregation";


function formatScore(value: number | null) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return Number(value).toFixed(2).replace(/\.00$/, "");
}

function formatPercent(value: number | null) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return `${formatScore(value)}%`;
}

function avg(values: number[]) {
  if (!values.length) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function normalizedScoreSummary(values: number[], type: ScoreType): ScoreSummary {
  const normalizedValues = values
    .map((value) => normalizedScorePercent(value, type))
    .filter((value): value is number => value !== null);

  return {
    avg: avg(normalizedValues),
    count: normalizedValues.length,
  };
}

function getLocalizedText(en: string | null | undefined, de: string | null | undefined, lang: "en" | "de") {
  return lang === "de" ? de || en || "" : en || de || "";
}

function getModuleActionItems(module: ModuleRecommendationRow["modules"]) {
  return (module?.module_task_categories ?? [])
    .slice()
    .sort((a, b) => Number(a.order_index ?? 0) - Number(b.order_index ?? 0))
    .flatMap((category) => {
      const tasks = category.module_tasks ?? [];
      if (!tasks.length) {
        return [
          {
            textEn: category.title_en || "",
            textDe: category.title_de || category.title_en || "",
          },
        ];
      }

      return tasks.map((task) => ({
        textEn: task.task_en || category.title_en || "",
        textDe: task.task_de || category.title_de || task.task_en || category.title_en || "",
      }));
    })
    .filter((item) => item.textEn || item.textDe)
    .slice(0, 3);
}

function emptyAvailabilitySummary(): AvailabilitySummary {
  return {
    high: 0,
    medium: 0,
    low: 0,
    unknownMedicalDevice: 0,
    emergencyVolunteer: 0,
    childrenDependents: 0,
    careDependents: 0,
    noCarAccess: 0,
    publicTransportReliant: 0,
  };
}

function emptyScoreBreakdown(): ScoreBreakdown {
  return {
    PBG: { avg: null, count: 0 },
    Ext_PRS: { avg: null, count: 0 },
    E_Score: { avg: null, count: 0 },
  };
}

function emptyFlagSummary(): FlagSummary {
  return {
    spof: 0,
    medical: 0,
    care: 0,
    any: 0,
  };
}

function emptyIssueResolutionPotential(): IssueResolutionPotential {
  return {
    gainPct: null,
    issueFreeCount: 0,
    spofCount: 0,
    medicalCount: 0,
    careCount: 0,
    noCarCount: 0,
    total: 0,
  };
}

function summarizeIssueResolutionPotential(
  employees: ManagerRow[],
  flagsByEmployee: Map<string, AssessmentFlagRow>,
  availabilityByEmployee: Map<string, AvailabilityRow>
): IssueResolutionPotential {
  if (!employees.length) return emptyIssueResolutionPotential();

  let issueFreeCount = 0;
  let spofCount = 0;
  let medicalCount = 0;
  let careCount = 0;
  let noCarCount = 0;

  for (const employee of employees) {
    const flags = flagsByEmployee.get(employee.id);
    const availability = availabilityByEmployee.get(employee.id);
    const hasSpof = flags?.spof === true;
    const hasMedical = flags?.medical === true;
    const hasCare = availability?.has_care_dependents === true;
    const hasNoCar = availability?.has_car_access === false;

    if (hasSpof) spofCount += 1;
    if (hasMedical) medicalCount += 1;
    if (hasCare) careCount += 1;
    if (hasNoCar) noCarCount += 1;
    if (!hasSpof && !hasMedical && !hasCare && !hasNoCar) issueFreeCount += 1;
  }

  return {
    gainPct: Math.max(0, 100 - percent(issueFreeCount, employees.length)),
    issueFreeCount,
    spofCount,
    medicalCount,
    careCount,
    noCarCount,
    total: employees.length,
  };
}

function summarizeAvailability(rows: AvailabilityRow[]): AvailabilitySummary {
  return rows.reduce((summary, row) => {
    if (row.availability_level === "high") summary.high += 1;
    else if (row.availability_level === "medium") summary.medium += 1;
    else if (row.availability_level === "low") summary.low += 1;

    if (row.medical_device_electricity_dependency === null) summary.unknownMedicalDevice += 1;
    if (row.emergency_volunteer_active === true) summary.emergencyVolunteer += 1;
    if (row.has_children_dependents === true) summary.childrenDependents += 1;
    if (row.has_care_dependents === true) summary.careDependents += 1;
    if (row.has_car_access === false) summary.noCarAccess += 1;
    if (row.public_transport_reliant === true) summary.publicTransportReliant += 1;

    return summary;
  }, emptyAvailabilitySummary());
}

function summarizeModuleRecommendations(
  rows: ModuleRecommendationRow[],
  employeeTotal: number,
  moduleImpacts: Map<string, ModuleImpactSummary>
): ModuleInsight[] {
  const byModule = new Map<
    string,
    Omit<ModuleInsight, "employeePct" | "topReasons"> & {
      employeeIds: Set<string>;
      reasonCounts: Map<string, number>;
    }
  >();

  for (const row of rows) {
    if (!row.module_id) continue;

    const existing =
      byModule.get(row.module_id) ??
      {
        moduleId: row.module_id,
        order: Number(row.module_order ?? 0),
        titleEn: row.modules?.name_en || `Module ${row.module_order}`,
        titleDe: row.modules?.name_de || row.modules?.name_en || `Modul ${row.module_order}`,
        descriptionEn: row.modules?.description_en || "",
        descriptionDe: row.modules?.description_de || row.modules?.description_en || "",
        employeeCount: 0,
        impactPct: null,
        impactCount: 0,
        impactCompletedCount: 0,
        estimatedMinutes:
          typeof row.modules?.estimated_minutes === "number"
            ? row.modules.estimated_minutes
            : row.modules?.estimated_minutes === null || row.modules?.estimated_minutes === undefined
            ? null
            : Number(row.modules.estimated_minutes),
        actionItems: getModuleActionItems(row.modules),
        employeeIds: new Set<string>(),
        reasonCounts: new Map<string, number>(),
      };

    if (row.employee_id) existing.employeeIds.add(row.employee_id);

    for (const reason of row.trigger_reasons ?? []) {
      if (!reason) continue;
      existing.reasonCounts.set(reason, (existing.reasonCounts.get(reason) ?? 0) + 1);
    }

    byModule.set(row.module_id, existing);
  }

  return Array.from(byModule.values())
    .map((module) => {
      const employeeCount = module.employeeIds.size;
      const impact = moduleImpacts.get(module.moduleId);
      const impactPct =
        impact?.avgGainPct !== null && impact?.avgGainPct !== undefined && !Number.isNaN(Number(impact.avgGainPct))
          ? Number(impact.avgGainPct)
          : null;
      const estimatedMinutes =
        module.estimatedMinutes !== null && !Number.isNaN(Number(module.estimatedMinutes))
          ? Number(module.estimatedMinutes)
          : null;

      return {
        moduleId: module.moduleId,
        order: module.order,
        titleEn: module.titleEn,
        titleDe: module.titleDe,
        descriptionEn: module.descriptionEn,
        descriptionDe: module.descriptionDe,
        employeeCount,
        employeePct: percent(employeeCount, employeeTotal),
        impactPct,
        impactCount: impact?.count ?? 0,
        impactCompletedCount: impact?.completedCount ?? 0,
        estimatedMinutes,
        actionItems: module.actionItems,
        topReasons: Array.from(module.reasonCounts.entries())
          .map(([reason, count]) => ({ reason, count }))
          .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason))
          .slice(0, 3),
      };
    })
    .sort((a, b) => b.employeeCount - a.employeeCount || a.order - b.order);
}

function calculateModuleImpactByModule(args: {
  recommendations: ModuleRecommendationRow[];
  scoresByEmployee: Map<string, Partial<Record<ScoreType, number>>>;
  flagsByEmployee: Map<string, AssessmentFlagRow>;
  pbgQuestions: PbgQuestionRow[];
  extPrsQuestions: ExtPrsQuestionRow[];
  pbgAnswers: PbgAnswerRow[];
  extPrsAnswers: ExtPrsAnswerRow[];
  answerImprovements: AnswerImprovementRow[];
  moduleTaskCategories: ModuleTaskCategoryRow[];
  moduleTaskProgress: ModuleTaskProgressRow[];
}) {
  const context = createModuleImpactContext(args);
  const gainsByModule = new Map<string, number[]>();
  const completedByModule = new Map<string, number>();
  const moduleIds = new Set<string>();

  for (const recommendation of args.recommendations) {
    moduleIds.add(recommendation.module_id);
    if (isModuleCompletedForEmployee(recommendation.employee_id, recommendation.module_id, context)) {
      completedByModule.set(
        recommendation.module_id,
        (completedByModule.get(recommendation.module_id) ?? 0) + 1
      );
      continue;
    }

    const directQuestionCodes = new Set(
      expandRecommendationQuestionCodes(
        recommendation,
        args.flagsByEmployee.get(recommendation.employee_id)
      )
    );
    const gain = calculateProjectedGainForEmployee(
      recommendation.employee_id,
      new Set([recommendation.module_id]),
      context,
      directQuestionCodes
    );

    if (gain === null) continue;

    const gains = gainsByModule.get(recommendation.module_id) ?? [];
    gains.push(gain);
    gainsByModule.set(recommendation.module_id, gains);
  }

  return new Map(
    Array.from(moduleIds).map((moduleId) => {
      const gains = gainsByModule.get(moduleId) ?? [];
      return [
        moduleId,
        {
          avgGainPct: avg(gains),
          count: gains.length,
          completedCount: completedByModule.get(moduleId) ?? 0,
        },
      ] as [string, ModuleImpactSummary];
    })
  );
}

function calculateCombinedModuleImpact(args: {
  recommendations: ModuleRecommendationRow[];
  scoresByEmployee: Map<string, Partial<Record<ScoreType, number>>>;
  flagsByEmployee: Map<string, AssessmentFlagRow>;
  pbgQuestions: PbgQuestionRow[];
  extPrsQuestions: ExtPrsQuestionRow[];
  pbgAnswers: PbgAnswerRow[];
  extPrsAnswers: ExtPrsAnswerRow[];
  answerImprovements: AnswerImprovementRow[];
  moduleTaskCategories: ModuleTaskCategoryRow[];
  moduleTaskProgress: ModuleTaskProgressRow[];
  moduleIds: string[];
}) {
  if (!args.moduleIds.length) return null;

  const context = createModuleImpactContext(args);
  const moduleIdsToComplete = new Set(args.moduleIds);
  const moduleIdsForPrediction = new Set(args.moduleIds);
  const directQuestionCodesByEmployee = new Map<string, Set<string>>();
  const gains: number[] = [];

  for (const recommendation of args.recommendations) {
    if (!moduleIdsForPrediction.has(recommendation.module_id)) continue;

    const questionCodes = directQuestionCodesByEmployee.get(recommendation.employee_id) ?? new Set<string>();
    for (const questionCode of expandRecommendationQuestionCodes(
      recommendation,
      args.flagsByEmployee.get(recommendation.employee_id)
    )) {
      questionCodes.add(questionCode);
    }
    directQuestionCodesByEmployee.set(recommendation.employee_id, questionCodes);
  }

  for (const [employeeId, scores] of Array.from(args.scoresByEmployee.entries())) {
    if (scores.PBG === undefined || scores.Ext_PRS === undefined || scores.E_Score === undefined) continue;

    const gain = calculateProjectedGainForEmployee(
      employeeId,
      moduleIdsToComplete,
      context,
      directQuestionCodesByEmployee.get(employeeId)
    );
    if (gain !== null) gains.push(gain);
  }

  return avg(gains);
}

function createModuleImpactContext(args: {
  recommendations: ModuleRecommendationRow[];
  scoresByEmployee: Map<string, Partial<Record<ScoreType, number>>>;
  flagsByEmployee: Map<string, AssessmentFlagRow>;
  pbgQuestions: PbgQuestionRow[];
  extPrsQuestions: ExtPrsQuestionRow[];
  pbgAnswers: PbgAnswerRow[];
  extPrsAnswers: ExtPrsAnswerRow[];
  answerImprovements: AnswerImprovementRow[];
  moduleTaskCategories: ModuleTaskCategoryRow[];
  moduleTaskProgress: ModuleTaskProgressRow[];
}): ModuleImpactContext {
  const pbgQuestionById = new Map(args.pbgQuestions.map((question) => [question.id, question]));
  const pbgQuestionByCode = new Map(
    args.pbgQuestions
      .filter((question) => Boolean(question.question_code))
      .map((question) => [String(question.question_code), question])
  );
  const extPrsQuestionById = new Map(args.extPrsQuestions.map((question) => [question.id, question]));
  const extPrsQuestionByCode = new Map(
    args.extPrsQuestions
      .filter((question) => Boolean(question.question_code))
      .map((question) => [String(question.question_code), question])
  );
  const pbgAnswersByEmployee = new Map<string, Map<string, string>>();
  const extPrsAnswersByEmployee = new Map<string, Map<string, string>>();
  const improvementsByEmployee = new Map<string, Map<string, string>>();
  const doneTasksByEmployee = new Map<string, Set<string>>();
  const taskIdsByModule = new Map<string, Set<string>>();
  const expandedRecommendations = new Map<string, Map<string, Set<string>>>();

  for (const answer of args.pbgAnswers) {
    const question = pbgQuestionById.get(answer.pbg_question_id);
    if (!answer.employee_id || !question?.question_code || !answer.given_answer) continue;

    const answers = pbgAnswersByEmployee.get(answer.employee_id) ?? new Map<string, string>();
    answers.set(question.question_code, answer.given_answer);
    pbgAnswersByEmployee.set(answer.employee_id, answers);
  }

  for (const answer of args.extPrsAnswers) {
    const question = extPrsQuestionById.get(answer.extprs_question_id);
    if (!answer.employee_id || !question?.question_code || !answer.given_answer) continue;

    const answers = extPrsAnswersByEmployee.get(answer.employee_id) ?? new Map<string, string>();
    answers.set(question.question_code, answer.given_answer);
    extPrsAnswersByEmployee.set(answer.employee_id, answers);
  }

  for (const improvement of args.answerImprovements) {
    if (!improvement.employee_id || !improvement.question_code || !improvement.improved_answer) continue;

    const improvements = improvementsByEmployee.get(improvement.employee_id) ?? new Map<string, string>();
    improvements.set(improvement.question_code, improvement.improved_answer);
    improvementsByEmployee.set(improvement.employee_id, improvements);
  }

  for (const category of args.moduleTaskCategories) {
    if (!category.module_id) continue;

    const taskIds = taskIdsByModule.get(category.module_id) ?? new Set<string>();
    for (const task of category.module_tasks ?? []) {
      if (task.id) taskIds.add(task.id);
    }
    taskIdsByModule.set(category.module_id, taskIds);
  }

  for (const progress of args.moduleTaskProgress) {
    if (!progress.employee_id || !progress.module_task_id || progress.is_done !== true) continue;

    const taskIds = doneTasksByEmployee.get(progress.employee_id) ?? new Set<string>();
    taskIds.add(progress.module_task_id);
    doneTasksByEmployee.set(progress.employee_id, taskIds);
  }

  for (const recommendation of args.recommendations) {
    if (!recommendation.employee_id || !recommendation.module_id) continue;

    const byQuestion = expandedRecommendations.get(recommendation.employee_id) ?? new Map<string, Set<string>>();
    for (const questionCode of expandRecommendationQuestionCodes(
      recommendation,
      args.flagsByEmployee.get(recommendation.employee_id)
    )) {
      const moduleIds = byQuestion.get(questionCode) ?? new Set<string>();
      moduleIds.add(recommendation.module_id);
      byQuestion.set(questionCode, moduleIds);
    }
    expandedRecommendations.set(recommendation.employee_id, byQuestion);
  }

  return {
    recommendations: args.recommendations,
    scoresByEmployee: args.scoresByEmployee,
    pbgQuestions: args.pbgQuestions,
    extPrsQuestions: args.extPrsQuestions,
    pbgQuestionById,
    pbgQuestionByCode,
    extPrsQuestionById,
    extPrsQuestionByCode,
    pbgAnswersByEmployee,
    extPrsAnswersByEmployee,
    improvementsByEmployee,
    doneTasksByEmployee,
    taskIdsByModule,
    expandedRecommendations,
  };
}

function isModuleCompletedForEmployee(employeeId: string, moduleId: string, context: ModuleImpactContext) {
  const taskIds = context.taskIdsByModule.get(moduleId);
  if (!taskIds || taskIds.size === 0) return false;

  const doneTaskIds = context.doneTasksByEmployee.get(employeeId) ?? new Set<string>();
  return Array.from(taskIds).every((taskId) => doneTaskIds.has(taskId));
}

function calculateProjectedGainForEmployee(
  employeeId: string,
  moduleIdsToComplete: Set<string>,
  context: ModuleImpactContext,
  directQuestionCodes?: Set<string>
) {
  const currentScores = context.scoresByEmployee.get(employeeId);

  if (
    !employeeId ||
    currentScores?.PBG === undefined ||
    currentScores.Ext_PRS === undefined ||
    currentScores.E_Score === undefined
  ) {
    return null;
  }

  const currentOverall = getOverallReadinessFromScores(currentScores as Record<ScoreType, number>);
  if (currentOverall === null) return null;

  const currentImprovements = context.improvementsByEmployee.get(employeeId) ?? new Map<string, string>();
  const projectedImprovements = new Map(currentImprovements);
  const relatedQuestions = context.expandedRecommendations.get(employeeId) ?? new Map<string, Set<string>>();
  const questionCodes = new Set([
    ...Array.from(relatedQuestions.keys()),
    ...Array.from(directQuestionCodes ?? []),
  ]);

  for (const questionCode of Array.from(questionCodes)) {
    const moduleIds = relatedQuestions.get(questionCode) ?? new Set<string>();
    const directUnlock = directQuestionCodes?.has(questionCode) === true;
    const usesProjectedModule = Array.from(moduleIds).some((moduleId) => moduleIdsToComplete.has(moduleId));
    if (!usesProjectedModule && !directUnlock) continue;

    const currentUnlocked =
      moduleIds.size > 0 &&
      Array.from(moduleIds).every((moduleId) =>
        isModuleCompletedForEmployee(employeeId, moduleId, context)
      );
    const projectedUnlocked =
      moduleIds.size > 0 &&
      Array.from(moduleIds).every((moduleId) =>
        moduleIdsToComplete.has(moduleId) || isModuleCompletedForEmployee(employeeId, moduleId, context)
      );

    if (currentUnlocked || (!projectedUnlocked && !directUnlock) || projectedImprovements.has(questionCode)) continue;

    const improvedAnswer = getProjectedImprovedAnswer({
      employeeId,
      questionCode,
      pbgQuestionByCode: context.pbgQuestionByCode,
      extPrsQuestionByCode: context.extPrsQuestionByCode,
      pbgAnswersByEmployee: context.pbgAnswersByEmployee,
      extPrsAnswersByEmployee: context.extPrsAnswersByEmployee,
    });

    if (improvedAnswer) projectedImprovements.set(questionCode, improvedAnswer);
  }

  const currentPbg = calculatePbgScoreForEmployee(
    employeeId,
    currentImprovements,
    context.pbgAnswersByEmployee,
    context.pbgQuestions
  );
  const projectedPbg = calculatePbgScoreForEmployee(
    employeeId,
    projectedImprovements,
    context.pbgAnswersByEmployee,
    context.pbgQuestions
  );
  const currentExtPrsOriginal = calculateExtPrsOriginalScoreForEmployee(
    employeeId,
    currentImprovements,
    context.extPrsAnswersByEmployee,
    context.extPrsQuestions
  );
  const projectedExtPrsOriginal = calculateExtPrsOriginalScoreForEmployee(
    employeeId,
    projectedImprovements,
    context.extPrsAnswersByEmployee,
    context.extPrsQuestions
  );

  const projectedScores: Record<ScoreType, number> = {
    PBG:
      currentPbg !== null && projectedPbg !== null
        ? Number(currentScores.PBG) + (projectedPbg - currentPbg)
        : Number(currentScores.PBG),
    Ext_PRS:
      currentExtPrsOriginal !== null && projectedExtPrsOriginal !== null
        ? Number(currentScores.Ext_PRS) + (projectedExtPrsOriginal - currentExtPrsOriginal)
        : Number(currentScores.Ext_PRS),
    E_Score: Number(currentScores.E_Score),
  };
  const projectedOverall = getOverallReadinessFromScores(projectedScores);
  if (projectedOverall === null) return null;

  return projectedOverall - currentOverall;
}

function expandRecommendationQuestionCodes(
  recommendation: Pick<ModuleRecommendationRow, "trigger_reasons">,
  flags: AssessmentFlagRow | undefined
) {
  const questionCodes = new Set<string>();

  for (const reason of recommendation.trigger_reasons ?? []) {
    if (/^Q[0-9]+$/.test(reason)) questionCodes.add(reason);
    else if (reason === "Q24-Q27") ["Q24", "Q25", "Q26", "Q27"].forEach((code) => questionCodes.add(code));
    else if (reason === "P37+Q35") questionCodes.add("Q35");
    else if (reason === "Medical flag") questionCodes.add("Q40");
    else if (reason === "SPoF flag") {
      for (const code of flags?.spof_reasons ?? []) {
        if (code) questionCodes.add(code);
      }
    }
  }

  return Array.from(questionCodes);
}

function getProjectedImprovedAnswer(args: {
  employeeId: string;
  questionCode: string;
  pbgQuestionByCode: Map<string, PbgQuestionRow>;
  extPrsQuestionByCode: Map<string, ExtPrsQuestionRow>;
  pbgAnswersByEmployee: Map<string, Map<string, string>>;
  extPrsAnswersByEmployee: Map<string, Map<string, string>>;
}) {
  const pbgQuestion = args.pbgQuestionByCode.get(args.questionCode);
  if (pbgQuestion) {
    const originalAnswer = args.pbgAnswersByEmployee.get(args.employeeId)?.get(args.questionCode);
    const improvedAnswer = originalAnswer
      ? getBestProjectedPbgAnswer(pbgQuestion, args.questionCode, originalAnswer)
      : null;

    return improvedAnswer && improvedAnswer !== originalAnswer ? improvedAnswer : null;
  }

  const extPrsQuestion = args.extPrsQuestionByCode.get(args.questionCode);
  if (extPrsQuestion) {
    const originalAnswer = args.extPrsAnswersByEmployee.get(args.employeeId)?.get(args.questionCode);
    const improvedAnswer = originalAnswer
      ? getBestProjectedExtPrsAnswer(extPrsQuestion, args.questionCode, originalAnswer)
      : null;

    return improvedAnswer && improvedAnswer !== originalAnswer ? improvedAnswer : null;
  }

  return null;
}

function calculatePbgScoreForEmployee(
  employeeId: string,
  improvements: Map<string, string>,
  answersByEmployee: Map<string, Map<string, string>>,
  questions: PbgQuestionRow[]
) {
  const answers = answersByEmployee.get(employeeId) ?? new Map<string, string>();
  const groups: Record<string, number[]> = { G: [], R: [], V: [], I: [] };

  for (const question of questions) {
    const questionCode = question.question_code;
    const pbgType = question.pbg_type ?? "";
    if (!questionCode || !groups[pbgType]) continue;

    const answer = improvements.get(questionCode) ?? answers.get(questionCode) ?? null;
    const score =
      getPbgQuestionScoreForAnswer(question, answer) ??
      getPbgDefaultQuestionScore(questionCode, Number(question.question_weight ?? 0));
    groups[pbgType].push(score);
  }

  const g = avg(groups.G);
  const r = avg(groups.R);
  const v = avg(groups.V);
  const i = avg(groups.I);

  if (g === null || r === null || v === null || i === null || i === 0) return null;
  return (g * r * v) / i;
}

function calculateExtPrsOriginalScoreForEmployee(
  employeeId: string,
  improvements: Map<string, string>,
  answersByEmployee: Map<string, Map<string, string>>,
  questions: ExtPrsQuestionRow[]
) {
  const answers = answersByEmployee.get(employeeId) ?? new Map<string, string>();
  let total = 0;
  let scoredCount = 0;

  for (const question of questions) {
    if (question.is_scored !== true || !question.question_code) continue;

    const answer = improvements.get(question.question_code) ?? answers.get(question.question_code) ?? null;
    const points =
      getExtPrsPointsForAnswer(question, answer) ?? getExtPrsDefaultAnswerPoints(question.question_code);
    total += points;
    scoredCount += 1;
  }

  return scoredCount > 0 ? total : null;
}

function getPbgQuestionScoreForAnswer(question: PbgQuestionRow, answer: string | null) {
  if (!answer) return null;

  const modifier = getAnswerOptionNumber(question.answer_options, answer);
  if (modifier === null || !question.pbg_type) return null;

  return Number(question.question_weight ?? 0) * modifier;
}

function getPbgDefaultQuestionScore(questionCode: string, questionWeight: number) {
  if (/^Q(?:[5-9]|1[0-9]|2[0-3])$/.test(questionCode)) return questionWeight;

  const defaults: Record<string, number> = {
    Q24: 0.7,
    Q25: 0.75,
    Q26: 0.7,
    Q27: 0.72,
  };

  return questionWeight * (defaults[questionCode] ?? 0);
}

function getExtPrsPointsForAnswer(question: ExtPrsQuestionRow, answer: string | null) {
  if (!answer) return null;
  return getAnswerOptionNumber(question.answer_options, answer);
}

function getExtPrsDefaultAnswerPoints(questionCode: string) {
  const defaults: Record<string, number> = {
    Q31: 3,
    Q32: 3,
    Q33: 2,
    Q34: 4,
    Q35: 3,
    Q36: 3,
    Q37: 2,
    Q38: 3,
    Q39: 3,
    Q40: 0,
    Q41: 3,
    Q42: 3,
    Q43: 2,
    Q44: 3,
    Q45: 3,
  };

  return defaults[questionCode] ?? 0;
}

function getBestProjectedPbgAnswer(question: PbgQuestionRow, questionCode: string, currentAnswer: string) {
  if (questionCode === "Q30") {
    if (currentAnswer === "3" || currentAnswer === "2") return "1";
    return currentAnswer;
  }

  const currentModifier = getAnswerOptionNumber(question.answer_options, currentAnswer);
  if (currentModifier === null || !question.pbg_type) return currentAnswer;

  const candidates = getEnglishAnswerOptions(question.answer_options)
    .filter((option) =>
      question.pbg_type === "I" ? option.value > currentModifier : option.value < currentModifier
    )
    .sort((a, b) =>
      question.pbg_type === "I" ? b.value - a.value : a.value - b.value
    );

  return candidates[0]?.answer ?? currentAnswer;
}

function getBestProjectedExtPrsAnswer(question: ExtPrsQuestionRow, questionCode: string, currentAnswer: string) {
  if (questionCode === "Q40") {
    return currentAnswer === "C" ? "B" : currentAnswer;
  }

  const currentPoints = getExtPrsPointsForAnswer(question, currentAnswer);
  if (currentPoints === null) return currentAnswer;

  const candidates = getEnglishAnswerOptions(question.answer_options)
    .filter((option) => option.value < currentPoints)
    .sort((a, b) => a.value - b.value);

  return candidates[0]?.answer ?? currentAnswer;
}

function getAnswerOptionNumber(options: AnswerOptions | null, answer: string) {
  const rawValue = options?.en?.[answer] ?? options?.de?.[answer];
  const value = Number(rawValue);

  return Number.isFinite(value) ? value : null;
}

function getEnglishAnswerOptions(options: AnswerOptions | null) {
  return Object.entries(options?.en ?? {})
    .map(([answer, rawValue]) => ({ answer, value: Number(rawValue) }))
    .filter((option) => Number.isFinite(option.value));
}

function clampPercent(value: number) {
  return Math.max(0, Math.min(100, value));
}

function normalizedScorePercent(value: number | null, type: ScoreType) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;

  if (type === "PBG") {
    return clampPercent(((12.15 - value) / (12.15 - 4.19)) * 100);
  }

  if (type === "Ext_PRS") {
    return clampPercent(((50 - value) / 50) * 100);
  }

  return clampPercent((value / 3.08) * 100);
}

function getOverallReadinessFromScores(scores: Partial<Record<ScoreType, number>>) {
  const pbgNorm = normalizedScorePercent(scores.PBG ?? null, "PBG");
  const prsNorm = normalizedScorePercent(scores.Ext_PRS ?? null, "Ext_PRS");
  const eNorm = normalizedScorePercent(scores.E_Score ?? null, "E_Score");

  if (pbgNorm === null || prsNorm === null || eNorm === null) return null;

  return clampPercent(pbgNorm * 0.35 + prsNorm * 0.35 + eNorm * 0.3);
}

function hexToRgb(hex: string) {
  const clean = hex.replace("#", "");

  return {
    r: parseInt(clean.substring(0, 2), 16),
    g: parseInt(clean.substring(2, 4), 16),
    b: parseInt(clean.substring(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number) {
  const toHex = (n: number) => Math.round(n).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function mixHex(a: string, b: string, t: number) {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);

  return rgbToHex(
    ca.r + (cb.r - ca.r) * t,
    ca.g + (cb.g - ca.g) * t,
    ca.b + (cb.b - ca.b) * t
  );
}

function colorOnScale(type: ScoreType, position: number) {
  const p = Math.max(0, Math.min(100, position));

  void type;

  const stops = [
    { at: 0, color: "#DC2626" },
    { at: 28, color: "#EA580C" },
    { at: 52, color: "#D6B866" },
    { at: 75, color: "#84CC16" },
    { at: 100, color: "#16A34A" },
  ];

  for (let i = 0; i < stops.length - 1; i += 1) {
    const current = stops[i];
    const next = stops[i + 1];

    if (p >= current.at && p <= next.at) {
      const localT = (p - current.at) / (next.at - current.at);
      return mixHex(current.color, next.color, localT);
    }
  }

  return stops[stops.length - 1].color;
}

function SectionIconBox(props: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2.5,
        bgcolor: "#FFFFFF",
        border: "1px solid #E5E7EB",
        display: "grid",
        placeItems: "center",
        color: "#111827",
        flexShrink: 0,
      }}
    >
      {props.children}
    </Box>
  );
}

export default function ManagerDashboard(props: { handlePageChange: (page: string) => void }) {
  void props;

  const { lang } = useI18n();
  const typedLang: "en" | "de" = lang === "de" ? "de" : "en";
  const L = React.useCallback(
    (en: string, de: string) => (typedLang === "de" ? de : en),
    [typedLang]
  );

  const [loading, setLoading] = React.useState(true);
  const [uiError, setUiError] = React.useState<string | null>(null);
  const [viewerRole, setViewerRole] = React.useState<"manager" | "admin">("manager");
  const [viewerCompanyId, setViewerCompanyId] = React.useState<string | null>(null);
  const [companies, setCompanies] = React.useState<CompanyRow[]>([]);
  const [companyEmployeeCounts, setCompanyEmployeeCounts] = React.useState<Record<string, number>>(
    {}
  );
  const [selectedCompanyId, setSelectedCompanyId] = React.useState<string | null>(null);
  const [adminSection, setAdminSection] = React.useState<"companies" | "events">("companies");
  const [events, setEvents] = React.useState<EventRow[]>([]);
  const [eventParticipantCounts, setEventParticipantCounts] = React.useState<Record<string, number>>({});
  const [selectedEventId, setSelectedEventId] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<"overview" | "departments" | "plan" | "feedback" | "contact">(
    "overview"
  );
  const [companyContact, setCompanyContact] = React.useState<CompanyEmergencyContact | null>(null);
  const [contactForm, setContactForm] = React.useState({ name: "", email: "", phone_number: "" });
  const [contactSaving, setContactSaving] = React.useState(false);
  const [contactMessage, setContactMessage] = React.useState<{ severity: "success" | "error"; text: string } | null>(
    null
  );

  const [employeeCount, setEmployeeCount] = React.useState(0);
  const [companyOverallScore, setCompanyOverallScore] = React.useState<ScoreSummary>({
    avg: null,
    count: 0,
  });
  const [companyScoreBreakdown, setCompanyScoreBreakdown] = React.useState<ScoreBreakdown>(() =>
    emptyScoreBreakdown()
  );
  const [departmentRows, setDepartmentRows] = React.useState<DepartmentSummary[]>([]);
  const [completedAllCount, setCompletedAllCount] = React.useState(0);
  const [flagSummary, setFlagSummary] = React.useState<FlagSummary>(() => emptyFlagSummary());
  const [availabilitySummary, setAvailabilitySummary] = React.useState<AvailabilitySummary>(() =>
    emptyAvailabilitySummary()
  );
  const [moduleInsights, setModuleInsights] = React.useState<ModuleInsight[]>([]);
  const [moduleMaxPredictionGain, setModuleMaxPredictionGain] = React.useState<number | null>(null);
  const [issueResolutionPotential, setIssueResolutionPotential] = React.useState<IssueResolutionPotential>(() =>
    emptyIssueResolutionPotential()
  );

  React.useEffect(() => {
    let mounted = true;

    async function loadManagerStats() {
      setLoading(true);
      setUiError(null);

      try {
        const { data: userData, error: userErr } = await getCurrentUser();

        if (userErr) throw userErr;
        if (!userData?.user) {
          throw new Error(L("Not authenticated.", "Nicht angemeldet."));
        }

        const { data: me, error: meErr } = await fetchManagerViewerByAuthUserId(userData.user.id);

        if (meErr) throw meErr;
        if (!me?.id) {
          throw new Error(
            L(
              "No employee profile is linked to this account.",
              "Kein Mitarbeiterprofil ist mit diesem Konto verknüpft."
            )
          );
        }

        if (!["manager", "admin"].includes(String(me.company_role))) {
          throw new Error(L("You do not have manager access.", "Du hast keinen Manager-Zugriff."));
        }

        const nextViewerRole = String(me.company_role) === "admin" ? "admin" : "manager";
        setViewerRole(nextViewerRole);
        setViewerCompanyId((me.company_id as string | null) ?? null);

        let targetCompanyId = me.company_id as string | null;

        if (nextViewerRole === "admin") {
          const [{ data: companyRows, error: companiesErr }, { data: employeeCompanyRows, error: countsErr }] =
            await Promise.all([fetchCompanies(), fetchEmployeeCompanyIds()]);

          if (companiesErr) throw companiesErr;
          if (countsErr) throw countsErr;

          const nextCompanies = (companyRows ?? []) as CompanyRow[];
          const nextCounts = ((employeeCompanyRows ?? []) as { company_id: string | null }[]).reduce(
            (counts, row) => {
              if (row.company_id) {
                counts[row.company_id] = (counts[row.company_id] ?? 0) + 1;
              }
              return counts;
            },
            {} as Record<string, number>
          );

          if (!mounted) return;

          setCompanies(nextCompanies);
          setCompanyEmployeeCounts(nextCounts);
          targetCompanyId = selectedCompanyId;

          if (!targetCompanyId) {
            setEmployeeCount(0);
            setCompanyOverallScore({ avg: null, count: 0 });
            setCompanyScoreBreakdown(emptyScoreBreakdown());
            setDepartmentRows([]);
            setCompletedAllCount(0);
            setFlagSummary(emptyFlagSummary());
            setAvailabilitySummary(emptyAvailabilitySummary());
            setModuleInsights([]);
            setModuleMaxPredictionGain(null);
            setIssueResolutionPotential(emptyIssueResolutionPotential());
            return;
          }
        }

        if (!targetCompanyId) {
          throw new Error(
            L(
              "No company is linked to this account.",
              "Diesem Konto ist kein Unternehmen zugeordnet."
            )
          );
        }

        const { data: emergencyContact, error: emergencyContactErr } =
          await fetchCompanyEmergencyContact(targetCompanyId);

        if (emergencyContactErr) throw emergencyContactErr;

        const { data: employees, error: employeesErr } = await fetchEmployeesByCompanyId(targetCompanyId);

        if (employeesErr) throw employeesErr;

        if (mounted) {
          const nextContact = (emergencyContact ?? null) as CompanyEmergencyContact | null;
          setCompanyContact(nextContact);
          setContactForm({
            name: nextContact?.name ?? "",
            email: nextContact?.email ?? "",
            phone_number: nextContact?.phone_number ?? "",
          });
        }

        const companyEmployees = ((employees ?? []) as ManagerRow[]).filter((e) => e.id);
        const employeeIds = companyEmployees.map((e) => e.id);

        if (!employeeIds.length) {
          if (!mounted) return;

          setEmployeeCount(0);
          setCompanyOverallScore({ avg: null, count: 0 });
          setCompanyScoreBreakdown(emptyScoreBreakdown());
          setDepartmentRows([]);
          setCompletedAllCount(0);
          setFlagSummary(emptyFlagSummary());
          setAvailabilitySummary(emptyAvailabilitySummary());
          setModuleInsights([]);
          setModuleMaxPredictionGain(null);
          setIssueResolutionPotential(emptyIssueResolutionPotential());
          return;
        }

        const { data: scoreRows, error: scoresErr } = await fetchKontrolScoresByEmployeeIds(employeeIds);

        if (scoresErr) throw scoresErr;

        const { data: flagRows, error: flagsErr } = await fetchAssessmentFlags(employeeIds);

        if (flagsErr) throw flagsErr;

        const { data: availabilityRows, error: availabilityErr } =
          await fetchManagerAvailabilityRows(employeeIds);

        if (availabilityErr) throw availabilityErr;

        const { data: moduleRecommendationRows, error: moduleRecommendationErr } =
          await fetchManagerModuleRecommendations(employeeIds);

        if (moduleRecommendationErr) throw moduleRecommendationErr;

        const [
          { data: pbgQuestionRows, error: pbgQuestionErr },
          { data: extPrsQuestionRows, error: extPrsQuestionErr },
          { data: pbgAnswerRows, error: pbgAnswerErr },
          { data: extPrsAnswerRows, error: extPrsAnswerErr },
          { data: answerImprovementRows, error: answerImprovementErr },
          { data: moduleTaskCategoryRows, error: moduleTaskCategoryErr },
          { data: moduleTaskProgressRows, error: moduleTaskProgressErr },
        ] = await fetchManagerImpactInputs(employeeIds);

        if (pbgQuestionErr) throw pbgQuestionErr;
        if (extPrsQuestionErr) throw extPrsQuestionErr;
        if (pbgAnswerErr) throw pbgAnswerErr;
        if (extPrsAnswerErr) throw extPrsAnswerErr;
        if (answerImprovementErr) throw answerImprovementErr;
        if (moduleTaskCategoryErr) throw moduleTaskCategoryErr;
        if (moduleTaskProgressErr) throw moduleTaskProgressErr;

        const scores = (scoreRows ?? []) as EmployeeScoreRow[];
        const flags = (flagRows ?? []) as AssessmentFlagRow[];
        const availability = (availabilityRows ?? []) as AvailabilityRow[];
        const moduleRecommendations = (moduleRecommendationRows ?? []) as unknown as ModuleRecommendationRow[];
        const flagsByEmployee = new Map<string, AssessmentFlagRow>();
        const availabilityByEmployee = new Map<string, AvailabilityRow>();

        for (const row of flags) {
          if (!row.employee_id) continue;
          flagsByEmployee.set(row.employee_id, row);
        }

        for (const row of availability) {
          if (!row.employee_id) continue;
          availabilityByEmployee.set(row.employee_id, row);
        }

        const companyAvailabilitySummary = summarizeAvailability(
          companyEmployees.map((employee) => availabilityByEmployee.get(employee.id)).filter(Boolean) as AvailabilityRow[]
        );
        const nextIssueResolutionPotential = summarizeIssueResolutionPotential(
          companyEmployees,
          flagsByEmployee,
          availabilityByEmployee
        );
        const companyFlagSummary: FlagSummary = companyEmployees.reduce(
          (summary, employee) => {
            const row = flagsByEmployee.get(employee.id);
            const availabilityRow = availabilityByEmployee.get(employee.id);
            const hasSpof = row?.spof === true;
            const hasMedical = row?.medical === true;
            const hasCare = availabilityRow?.has_care_dependents === true;

            if (hasSpof) summary.spof += 1;
            if (hasMedical) summary.medical += 1;
            if (hasCare) summary.care += 1;
            if (hasSpof || hasMedical || hasCare) summary.any += 1;

            return summary;
          },
          emptyFlagSummary()
        );

        const byEmployee = new Map<string, Partial<Record<ScoreType, number>>>();

        for (const row of scores) {
          if (!row.employee_id || !row.score_type) continue;

          const numericScore =
            typeof row.kontrol_score === "number"
              ? row.kontrol_score
              : Number(row.kontrol_score);

          if (Number.isNaN(numericScore)) continue;

          const existing = byEmployee.get(row.employee_id) ?? {};
          existing[row.score_type] = numericScore;
          byEmployee.set(row.employee_id, existing);
        }

        const overallValues = companyEmployees
          .map((employee) => getOverallReadinessFromScores(byEmployee.get(employee.id) ?? {}))
          .filter((value): value is number => value !== null);
        const pbgValues = scores
          .filter((r) => r.score_type === "PBG" && r.kontrol_score !== null)
          .map((r) => Number(r.kontrol_score))
          .filter((v) => !Number.isNaN(v));
        const extprsValues = scores
          .filter((r) => r.score_type === "Ext_PRS" && r.kontrol_score !== null)
          .map((r) => Number(r.kontrol_score))
          .filter((v) => !Number.isNaN(v));
        const eksValues = scores
          .filter((r) => r.score_type === "E_Score" && r.kontrol_score !== null)
          .map((r) => Number(r.kontrol_score))
          .filter((v) => !Number.isNaN(v));
        const completedAll = companyEmployees.filter((employee) => {
          const employeeScores = byEmployee.get(employee.id);

          return Boolean(
            employeeScores?.PBG !== undefined &&
              employeeScores?.Ext_PRS !== undefined &&
              employeeScores?.E_Score !== undefined
          );
        }).length;

        const deptMap = new Map<string, ManagerRow[]>();

        for (const employee of companyEmployees) {
          const dept =
            employee.department && employee.department.trim()
              ? employee.department.trim()
              : L("Unassigned", "Nicht zugeordnet");

          const list = deptMap.get(dept) ?? [];
          list.push(employee);
          deptMap.set(dept, list);
        }

        const deptSummaries: DepartmentSummary[] = Array.from(deptMap.entries())
          .map(([department, deptEmployees]) => {
            const deptFlags: FlagSummary = deptEmployees.reduce(
              (summary, employee) => {
                const row = flagsByEmployee.get(employee.id);
                const availabilityRow = availabilityByEmployee.get(employee.id);
                const hasSpof = row?.spof === true;
                const hasMedical = row?.medical === true;
                const hasCare = availabilityRow?.has_care_dependents === true;

                if (hasSpof) summary.spof += 1;
                if (hasMedical) summary.medical += 1;
                if (hasCare) summary.care += 1;
                if (hasSpof || hasMedical || hasCare) summary.any += 1;

                return summary;
              },
              emptyFlagSummary()
            );

            const deptAvailability = summarizeAvailability(
              deptEmployees
                .map((employee) => availabilityByEmployee.get(employee.id))
                .filter(Boolean) as AvailabilityRow[]
            );
            const deptOverall = deptEmployees
              .map((employee) => getOverallReadinessFromScores(byEmployee.get(employee.id) ?? {}))
              .filter((value): value is number => value !== null);

            return {
              department,
              employeeCount: deptEmployees.length,
              overall: { avg: avg(deptOverall), count: deptOverall.length },
              flags: deptFlags,
              availability: deptAvailability,
            };
          })
          .sort((a, b) => a.department.localeCompare(b.department));
        const moduleImpactInput = {
          recommendations: moduleRecommendations,
          scoresByEmployee: byEmployee,
          flagsByEmployee,
          pbgQuestions: (pbgQuestionRows ?? []) as PbgQuestionRow[],
          extPrsQuestions: (extPrsQuestionRows ?? []) as ExtPrsQuestionRow[],
          pbgAnswers: (pbgAnswerRows ?? []) as PbgAnswerRow[],
          extPrsAnswers: (extPrsAnswerRows ?? []) as ExtPrsAnswerRow[],
          answerImprovements: (answerImprovementRows ?? []) as AnswerImprovementRow[],
          moduleTaskCategories: (moduleTaskCategoryRows ?? []) as unknown as ModuleTaskCategoryRow[],
          moduleTaskProgress: (moduleTaskProgressRows ?? []) as ModuleTaskProgressRow[],
        };
        const moduleImpacts = calculateModuleImpactByModule(moduleImpactInput);
        const nextModuleInsights = summarizeModuleRecommendations(
          moduleRecommendations,
          companyEmployees.length,
          moduleImpacts
        );
        const nextMaxPredictionGain = calculateCombinedModuleImpact({
          ...moduleImpactInput,
          moduleIds: Array.from(new Set(moduleRecommendations.map((row) => row.module_id).filter(Boolean))),
        });

        if (!mounted) return;

        setEmployeeCount(companyEmployees.length);
        setCompanyOverallScore({ avg: avg(overallValues), count: overallValues.length });
        setCompanyScoreBreakdown({
          PBG: normalizedScoreSummary(pbgValues, "PBG"),
          Ext_PRS: normalizedScoreSummary(extprsValues, "Ext_PRS"),
          E_Score: normalizedScoreSummary(eksValues, "E_Score"),
        });
        setDepartmentRows(deptSummaries);
        setCompletedAllCount(completedAll);
        setFlagSummary(companyFlagSummary);
        setAvailabilitySummary(companyAvailabilitySummary);
        setModuleInsights(nextModuleInsights);
        setModuleMaxPredictionGain(nextMaxPredictionGain);
        setIssueResolutionPotential(nextIssueResolutionPotential);
      } catch (e: any) {
        if (!mounted) return;

        setUiError(
          e?.message ??
            L(
              "Failed to load manager statistics.",
              "Manager-Statistiken konnten nicht geladen werden."
            )
        );
        setEmployeeCount(0);
        setCompanyOverallScore({ avg: null, count: 0 });
        setCompanyScoreBreakdown(emptyScoreBreakdown());
        setDepartmentRows([]);
        setCompletedAllCount(0);
        setFlagSummary(emptyFlagSummary());
        setAvailabilitySummary(emptyAvailabilitySummary());
        setModuleInsights([]);
        setModuleMaxPredictionGain(null);
        setIssueResolutionPotential(emptyIssueResolutionPotential());
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadManagerStats();

    return () => {
      mounted = false;
    };
  }, [L, selectedCompanyId]);

  React.useEffect(() => {
    if (viewerRole !== "admin") return;

    let mounted = true;

    async function loadEvents() {
      const [{ data: eventRows }, { data: counts }] = await Promise.all([
        fetchEvents(),
        fetchEventParticipantCounts(),
      ]);

      if (!mounted) return;

      setEvents((eventRows ?? []) as EventRow[]);
      setEventParticipantCounts(counts ?? {});
    }

    loadEvents();

    return () => {
      mounted = false;
    };
  }, [viewerRole]);

  const selectedCompany =
    companies.find((company) => company.id === selectedCompanyId) ?? null;
  const completionPct =
    employeeCount > 0 ? Math.round((completedAllCount / employeeCount) * 100) : 0;
  const activeCompanyId = viewerRole === "admin" ? selectedCompanyId : viewerCompanyId;
  const contactName = contactForm.name.trim();
  const contactEmail = contactForm.email.trim();
  const contactPhone = contactForm.phone_number.trim();
  const contactFormComplete = Boolean(contactName && contactEmail);

  const saveContact = async () => {
    const targetCompanyId = activeCompanyId;

    if (!targetCompanyId || !contactFormComplete) return;

    setContactSaving(true);
    setContactMessage(null);

    const { data, error } = await upsertCompanyEmergencyContact(targetCompanyId, {
      name: contactName,
      email: contactEmail,
      phone_number: contactPhone || null,
    });

    if (error) {
      setContactMessage({
        severity: "error",
        text: error.message || L("Could not save the company contact.", "Unternehmenskontakt konnte nicht gespeichert werden."),
      });
    } else {
      const nextContact = (data ?? null) as CompanyEmergencyContact | null;
      setCompanyContact(nextContact);
      setContactForm({
        name: nextContact?.name ?? "",
        email: nextContact?.email ?? "",
        phone_number: nextContact?.phone_number ?? "",
      });
      setContactMessage({
        severity: "success",
        text: L("Company contact saved.", "Unternehmenskontakt gespeichert."),
      });
    }

    setContactSaving(false);
  };

  const deleteContact = async () => {
    const targetCompanyId = activeCompanyId;

    if (!targetCompanyId) return;

    setContactSaving(true);
    setContactMessage(null);

    const { error } = await deleteCompanyEmergencyContact(targetCompanyId);

    if (error) {
      setContactMessage({
        severity: "error",
        text: error.message || L("Could not delete the company contact.", "Unternehmenskontakt konnte nicht gelöscht werden."),
      });
    } else {
      setCompanyContact(null);
      setContactForm({ name: "", email: "", phone_number: "" });
      setContactMessage({
        severity: "success",
        text: L("Company contact deleted.", "Unternehmenskontakt gelöscht."),
      });
    }

    setContactSaving(false);
  };

  if (viewerRole === "admin" && selectedEventId) {
    const selectedEvent = events.find((event) => event.id === selectedEventId) ?? null;

    if (selectedEvent) {
      return (
        <AdminEventDashboard
          event={selectedEvent}
          onBack={() => setSelectedEventId(null)}
          L={L}
          lang={typedLang}
        />
      );
    }
  }

  if (viewerRole === "admin" && !selectedCompanyId && !selectedEventId) {
    const sectionToggle = (
      <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto", pt: { xs: 2, md: 3 }, px: { xs: 2, md: 3 } }}>
        <ToggleButtonGroup
          exclusive
          value={adminSection}
          onChange={(_, next) => {
            if (next) setAdminSection(next);
          }}
          sx={{
            bgcolor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: 999,
            overflow: "hidden",
            "& .MuiToggleButton-root": {
              px: 2,
              py: 0.75,
              fontWeight: 900,
              textTransform: "none",
              border: 0,
              color: "#6B7280",
            },
            "& .Mui-selected": {
              color: "#FFFFFF !important",
              bgcolor: "#111827 !important",
            },
          }}
        >
          <ToggleButton value="companies">{L("Companies", "Unternehmen")}</ToggleButton>
          <ToggleButton value="events">{L("Events", "Anlässe")}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );

    if (adminSection === "companies") {
      return (
        <>
          {sectionToggle}
          <AdminCompanyList
            companies={companies}
            employeeCounts={companyEmployeeCounts}
            loading={loading}
            error={uiError}
            onSelect={setSelectedCompanyId}
            L={L}
          />
        </>
      );
    }

    return (
      <>
        {sectionToggle}
        <EventList
          events={events}
          participantCounts={eventParticipantCounts}
          loading={loading}
          error={uiError}
          onSelect={setSelectedEventId}
          L={L}
        />
      </>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F3F4F6", p: { xs: 2, md: 3 } }}>
      <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            {viewerRole === "admin" ? (
              <Button
                variant="outlined"
                startIcon={<ArrowBackRoundedIcon />}
                onClick={() => setSelectedCompanyId(null)}
                sx={{
                  mr: 0.5,
                  minHeight: 44,
                  borderRadius: 2.5,
                  borderColor: "#D1D5DB",
                  color: "#111827",
                  bgcolor: "#FFFFFF",
                  textTransform: "none",
                  fontWeight: 900,
                }}
              >
                {L("Companies", "Unternehmen")}
              </Button>
            ) : null}

            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                bgcolor: "#FFFFFF",
                border: "1px solid #E5E7EB",
                display: "grid",
                placeItems: "center",
                color: "#111827",
                flexShrink: 0,
              }}
            >
              <AdminPanelSettingsOutlinedIcon />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 900,
                  color: "#111827",
                  lineHeight: 1.1,
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {viewerRole === "admin"
                  ? selectedCompany?.name ?? L("Company Dashboard", "Unternehmens-Dashboard")
                  : L("Manager Dashboard", "Manager-Dashboard")}
              </Typography>

              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                {viewerRole === "admin"
                  ? L(
                      "Admin view of the company summary, department breakdown and recommended plan.",
                      "Admin-Ansicht der Unternehmensübersicht, Abteilungsaufschlüsselung und empfohlenen Planung."
                    )
                  : L(
                      "Company summary, department breakdown and recommended plan.",
                      "Unternehmensübersicht, Abteilungsaufschlüsselung und empfohlene Planung."
                    )}
              </Typography>
            </Box>
          </Stack>

          <Chip
            label={`${L("Employees", "Mitarbeitende")}: ${loading ? "…" : employeeCount}`}
            variant="outlined"
            sx={{
              height: 42,
              borderRadius: 2.5,
              borderColor: "#D1D5DB",
              bgcolor: "#FFFFFF",
              color: "#111827",
              fontWeight: 900,
              px: 1,
              minWidth: 180,
              justifyContent: "center",
            }}
          />
        </Stack>

        {uiError ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
            {uiError}
          </Alert>
        ) : null}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid #E5E7EB",
            bgcolor: "#FFFFFF",
            overflow: "hidden",
            mb: 2,
          }}
        >
          <Box sx={{ p: { xs: 1, sm: 1.25 }, display: "flex", justifyContent: "center" }}>
            <Tabs
              value={activeTab}
              onChange={(_event, value) => setActiveTab(value)}
              variant="fullWidth"
              sx={{
                width: "100%",
                minHeight: 44,
                bgcolor: "#F3F4F6",
                borderRadius: 2.5,
                p: 0.5,
                "& .MuiTabs-indicator": { display: "none" },
                "& .MuiTab-root": {
                  minHeight: 38,
                  borderRadius: 2,
                  color: "#6B7280",
                  fontWeight: 900,
                  textTransform: "none",
                },
                "& .Mui-selected": {
                  bgcolor: "#FFFFFF",
                  color: "#111827",
                  boxShadow: "0 1px 2px rgba(17,24,39,0.08)",
                },
              }}
            >
              <Tab value="overview" label={L("Overall view", "Gesamtansicht")} />
              <Tab value="departments" label={L("Department breakdown", "Abteilungsübersicht")} />
              <Tab value="plan" label={L("Recommended plan", "Empfohlener Plan")} />
              <Tab value="feedback" label={L("Team feedback", "Team-Feedback")} />
              <Tab value="contact" label={L("Company contact", "Unternehmenskontakt")} />
            </Tabs>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid #E5E7EB",
            bgcolor: "#FFFFFF",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            <Box
              sx={{
                borderRadius: 2.5,
                border: "1px solid #E5E7EB",
                bgcolor: "#F9FAFB",
                p: 2,
                mb: 2,
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <InfoOutlinedIcon sx={{ color: "#111827", mt: "2px" }} />
                <Box>
                  <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                    {L("How to read this", "So liest du die Werte")}
                  </Typography>

                  <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.25 }}>
                    {L(
                      "This dashboard keeps the manager view aggregated: availability, joined employees and employee preparedness. Individual employee scores are not displayed here.",
                      "Dieses Dashboard hält die Manageransicht aggregiert: Verfügbarkeit, beigetretene Mitarbeitende und Vorbereitung der Mitarbeitenden. Individuelle Mitarbeitenden-Scores werden hier nicht angezeigt."
                    )}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {loading ? (
              <Stack spacing={2}>
                <Skeleton variant="rounded" height={190} />
                <Skeleton variant="rounded" height={activeTab === "departments" ? 320 : 190} />
              </Stack>
            ) : activeTab === "overview" ? (
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                    gap: 2,
                    alignItems: "stretch",
                  }}
                >
                  <OperationalReadinessCard
                    summary={companyOverallScore}
                    scoreBreakdown={companyScoreBreakdown}
                    flags={flagSummary}
                    total={employeeCount}
                    L={L}
                  />

                  <AvailabilityParticipationCard
                    summary={availabilitySummary}
                    completionPct={completionPct}
                    completedAllCount={completedAllCount}
                    total={employeeCount}
                    L={L}
                  />
                </Box>

                <ReadinessPredictionSection
                  currentScore={companyOverallScore.avg}
                  currentScoreCount={companyOverallScore.count}
                  maxPredictionGain={moduleMaxPredictionGain}
                  issueResolutionPotential={issueResolutionPotential}
                  insights={moduleInsights.slice(0, 4)}
                  L={L}
                />

                <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
                  <Button
                    variant="contained"
                    endIcon={<ChevronRightRoundedIcon />}
                    onClick={() => setActiveTab("plan")}
                    sx={{
                      minHeight: 44,
                      borderRadius: 2.5,
                      bgcolor: "#111827",
                      color: "#FFFFFF",
                      textTransform: "none",
                      fontWeight: 900,
                      px: 2,
                      width: { xs: "100%", sm: "auto" },
                      "&:hover": { bgcolor: "#1F2937" },
                    }}
                  >
                    {L("Check your plan here", "Plan hier ansehen")}
                  </Button>
                </Box>
              </Stack>
            ) : activeTab === "departments" ? (
              <DepartmentBreakdown
                rows={departmentRows}
                employeeCount={employeeCount}
                L={L}
              />
            ) : activeTab === "plan" ? (
              <CompanyModuleSections
                insights={moduleInsights}
                employeeCount={employeeCount}
                currentScore={companyOverallScore.avg}
                currentScoreCount={companyOverallScore.count}
                maxPredictionGain={moduleMaxPredictionGain}
                issueResolutionPotential={issueResolutionPotential}
                lang={typedLang}
                L={L}
                showPrediction={false}
              />
            ) : activeTab === "feedback" ? (
              <TeamFeedbackPanel companyId={activeCompanyId} L={L} />
            ) : (
              <CompanyEmergencyContactPanel
                form={contactForm}
                hasSavedContact={Boolean(companyContact)}
                saving={contactSaving}
                disabled={!activeCompanyId}
                onChange={setContactForm}
                onSave={saveContact}
                onDelete={deleteContact}
                canSave={contactFormComplete}
                L={L}
              />
            )}
          </Box>
        </Paper>
      </Box>
      <Snackbar
        open={Boolean(contactMessage)}
        autoHideDuration={2200}
        onClose={() => setContactMessage(null)}
      >
        {contactMessage ? (
          <Alert severity={contactMessage.severity} onClose={() => setContactMessage(null)}>
            {contactMessage.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

// Company-scoped wrapper: resolves the manager's/admin's company into employee ids,
// then delegates to FeedbackInsightsPanel.
function TeamFeedbackPanel(props: { companyId: string | null; L: (en: string, de: string) => string }) {
  const { companyId, L } = props;

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [employeeIds, setEmployeeIds] = React.useState<string[]>([]);

  React.useEffect(() => {
    let mounted = true;

    async function load() {
      if (!companyId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { data: employees, error: employeesErr } = await fetchEmployeesByCompanyId(companyId);
        if (employeesErr) throw employeesErr;
        if (!mounted) return;

        setEmployeeIds((employees ?? []).map((e) => e.id as string));
      } catch (err) {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : L("Something went wrong.", "Etwas ist schiefgelaufen."));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [companyId, L]);

  if (loading) {
    return (
      <Stack spacing={1.5}>
        <Skeleton variant="rounded" height={90} />
        <Skeleton variant="rounded" height={90} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2.5 }}>
        {error}
      </Alert>
    );
  }

  return <FeedbackInsightsPanel employeeIds={employeeIds} L={L} />;
}

// Renders aggregated, internal-audience-only feedback for a fixed set of employee ids
// (a company's employees, or an event's participants) -- individual answers are never shown.
function FeedbackInsightsPanel(props: { employeeIds: string[]; L: (en: string, de: string) => string }) {
  const { employeeIds, L } = props;
  const { lang } = useI18n();
  const typedLang: "en" | "de" = lang === "de" ? "de" : "en";

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [aggregates, setAggregates] = React.useState<FeedbackAggregate[]>([]);

  const employeeIdsKey = employeeIds.join(",");

  React.useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        if (employeeIds.length === 0) {
          setAggregates([]);
          return;
        }

        const [{ data: questions, error: questionsErr }, { data: feedbackRows, error: feedbackErr }] =
          await Promise.all([fetchKontrolScoreFeedbackQuestions(), fetchKontrolScoreFeedbackByEmployeeIds(employeeIds)]);

        if (questionsErr) throw questionsErr;
        if (feedbackErr) throw feedbackErr;
        if (!mounted) return;

        const internalQuestions = ((questions ?? []) as KontrolScoreFeedbackQuestion[])
          .filter((q) => q.feedback_audience === "internal")
          .sort((a, b) => a.order_index - b.order_index);

        const answers = (feedbackRows ?? []).map((row) => row.answers ?? {});
        setAggregates(
          internalQuestions.map((question) => aggregateFeedbackQuestion(question, answers, typedLang, L))
        );
      } catch (err) {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : L("Something went wrong.", "Etwas ist schiefgelaufen."));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeIdsKey, typedLang, L]);

  const totalEmployees = employeeIds.length;

  if (loading) {
    return (
      <Stack spacing={1.5}>
        <Skeleton variant="rounded" height={90} />
        <Skeleton variant="rounded" height={90} />
        <Skeleton variant="rounded" height={90} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2.5 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
        <SectionIconBox>
          <RateReviewOutlinedIcon fontSize="small" />
        </SectionIconBox>

        <Box>
          <Typography sx={{ fontWeight: 900, color: "#111827" }}>
            {L("Team feedback", "Team-Feedback")}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280" }}>
            {L(
              "Aggregated preparedness feedback from your team -- individual answers are never shown here.",
              "Aggregiertes Vorbereitungs-Feedback deines Teams -- einzelne Antworten werden hier nie angezeigt."
            )}
          </Typography>
        </Box>
      </Stack>

      {aggregates.length === 0 ? (
        <Typography variant="body2" sx={{ color: "#6B7280", py: 2 }}>
          {L("No feedback submitted yet.", "Noch kein Feedback eingereicht.")}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {aggregates.map((aggregate) => (
            <Box
              key={aggregate.question.id}
              sx={{ borderRadius: 2.5, border: "1px solid #E5E7EB", bgcolor: "#F9FAFB", p: 2 }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={0.5}
              >
                <Typography sx={{ fontWeight: 800, color: "#111827" }}>
                  {labelForQuestion(aggregate.question, typedLang)}
                </Typography>
                <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 700, whiteSpace: "nowrap" }}>
                  {aggregate.respondentCount}/{totalEmployees} {L("responded", "geantwortet")}
                </Typography>
              </Stack>

              <Typography variant="body2" sx={{ color: "#111827", fontWeight: 700, mt: 1 }}>
                {aggregate.conclusion}
              </Typography>

              {aggregate.breakdown.length > 0 ? (
                <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                  {aggregate.breakdown.map((row) => (
                    <Box key={row.label}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }}>
                          {row.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 700 }}>
                          {row.pct}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={row.pct}
                        sx={{
                          mt: 0.35,
                          height: 6,
                          borderRadius: 20,
                          bgcolor: "#E5E7EB",
                          "& .MuiLinearProgress-bar": { bgcolor: "#111827", borderRadius: 20 },
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              ) : null}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function CompanyEmergencyContactPanel(props: {
  form: { name: string; email: string; phone_number: string };
  hasSavedContact: boolean;
  saving: boolean;
  disabled: boolean;
  canSave: boolean;
  onChange: (next: { name: string; email: string; phone_number: string }) => void;
  onSave: () => void;
  onDelete: () => void;
  L: (en: string, de: string) => string;
}) {
  const { form, hasSavedContact, saving, disabled, canSave, onChange, onSave, onDelete, L } = props;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <SectionIconBox>
          <ApartmentOutlinedIcon fontSize="small" />
        </SectionIconBox>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, color: "#111827" }}>
            {L("Company emergency contact", "Unternehmens-Notfallkontakt")}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280" }}>
            {L(
              "Shown to employees on their emergency page when name and email are set.",
              "Wird Mitarbeitenden auf der Notfallseite angezeigt, wenn Name und E-Mail hinterlegt sind."
            )}
          </Typography>
        </Box>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        <TextField
          label={L("Name", "Name")}
          value={form.name}
          required
          disabled={disabled || saving}
          onChange={(event) => onChange({ ...form, name: event.target.value })}
          inputProps={{ maxLength: 160 }}
        />
        <TextField
          label={L("Email", "E-Mail")}
          type="email"
          value={form.email}
          required
          disabled={disabled || saving}
          onChange={(event) => onChange({ ...form, email: event.target.value })}
          inputProps={{ maxLength: 320 }}
        />
        <TextField
          label={L("Phone optional", "Telefon optional")}
          value={form.phone_number}
          disabled={disabled || saving}
          onChange={(event) => onChange({ ...form, phone_number: event.target.value })}
          inputProps={{ maxLength: 80 }}
          sx={{ gridColumn: { xs: "auto", md: "1 / -1" } }}
        />
      </Box>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
        <Button
          variant="outlined"
          startIcon={<DeleteOutlineRoundedIcon />}
          onClick={onDelete}
          disabled={disabled || saving || !hasSavedContact}
          sx={{
            minHeight: 44,
            borderRadius: 2.5,
            borderColor: "#D1D5DB",
            color: "#111827",
            textTransform: "none",
            fontWeight: 900,
          }}
        >
          {L("Delete", "Löschen")}
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={onSave}
          disabled={disabled || saving || !canSave}
          sx={{
            minHeight: 44,
            borderRadius: 2.5,
            bgcolor: "#111827",
            color: "#FFFFFF",
            textTransform: "none",
            fontWeight: 900,
            "&:hover": { bgcolor: "#1F2937" },
          }}
        >
          {saving ? L("Saving...", "Speichert...") : L("Save contact", "Kontakt speichern")}
        </Button>
      </Stack>
    </Stack>
  );
}

function percent(value: number, total: number) {
  if (total <= 0) return 0;
  return Math.round((value / total) * 100);
}

function OperationalReadinessCard(props: {
  summary: ScoreSummary;
  scoreBreakdown: ScoreBreakdown;
  flags: FlagSummary;
  total: number;
  L: (en: string, de: string) => string;
}) {
  const { summary, scoreBreakdown, flags, total, L } = props;
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const progress = summary.avg ?? 0;
  const color = summary.avg === null ? "#9CA3AF" : colorOnScale("E_Score", progress);

  return (
    <Paper elevation={0} sx={overviewCardSx}>
      <Stack spacing={2} sx={{ height: "100%" }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <SectionIconBox>
            <TrendingUpOutlinedIcon fontSize="small" />
          </SectionIconBox>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography sx={overviewEyebrowSx}>
                {L("Operational readiness", "Operative Bereitschaft")}
              </Typography>
              <Box component="button" type="button" onClick={() => setDetailsOpen(true)} sx={detailsLinkSx}>
                {L("Details", "Details")} →
              </Box>
            </Stack>
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              {L(
                "Company preparedness percentage from completed employee inputs.",
                "Unternehmensweiter Vorbereitungsprozentsatz aus abgeschlossenen Mitarbeitenden-Eingaben."
              )}
            </Typography>
          </Box>
        </Stack>

        <Box>
          <Stack direction="row" spacing={0.75} alignItems="baseline">
            <Typography sx={{ fontWeight: 1000, color, fontSize: 42, lineHeight: 1 }}>
              {formatPercent(summary.avg)}
            </Typography>
          </Stack>

          <ReadinessGauge value={summary.avg} color={color} />
        </Box>

        <Box sx={{ mt: "auto" }}>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
            <Tooltip
              arrow
              title={L(
                "SPoF: critical work depends on one person.",
                "SPoF: Kritische Arbeit hängt von einer Person ab."
              )}
            >
              <Chip
                size="small"
                icon={<WarningAmberOutlinedIcon />}
                label={`SPoF ${flags.spof}/${total}`}
                sx={flagChipSx(flags.spof > 0 ? "#EA580C" : "#15803D")}
              />
            </Tooltip>
            <Tooltip
              arrow
              title={L(
                "Medical: health or mobility needs may affect availability.",
                "Medizin: Gesundheits- oder Mobilitätsbedarfe können die Verfügbarkeit beeinflussen."
              )}
            >
              <Chip
                size="small"
                icon={<MedicalServicesOutlinedIcon />}
                label={`${L("Medical", "Medizin")} ${flags.medical}/${total}`}
                sx={flagChipSx(flags.medical > 0 ? "#DC2626" : "#15803D")}
              />
            </Tooltip>
            <Tooltip
              arrow
              title={L(
                "Care: household care duties may affect availability.",
                "Pflege: Pflegeaufgaben im Haushalt können die Verfügbarkeit beeinflussen."
              )}
            >
              <Chip
                size="small"
                icon={<GroupsOutlinedIcon />}
                label={`${L("Care", "Pflege")} ${flags.care}/${total}`}
                sx={flagChipSx(flags.care > 0 ? "#7C3AED" : "#15803D")}
              />
            </Tooltip>
          </Stack>
        </Box>
      </Stack>

      <PreparednessDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        summary={summary}
        scoreBreakdown={scoreBreakdown}
        flags={flags}
        total={total}
        L={L}
      />
    </Paper>
  );
}

function AvailabilityParticipationCard(props: {
  summary: AvailabilitySummary;
  completionPct: number;
  completedAllCount: number;
  total: number;
  L: (en: string, de: string) => string;
}) {
  const { summary, completionPct, completedAllCount, total, L } = props;
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const knownTotal = summary.high + summary.medium + summary.low;
  const availablePct = percent(summary.high, total);
  const unknownCount = Math.max(0, total - knownTotal);
  const availabilityColor = colorOnScale("E_Score", availablePct);
  const completionColor =
    completionPct >= 75 ? "#16A34A" : completionPct >= 40 ? "#D6B866" : "#DC2626";

  return (
    <Paper elevation={0} sx={overviewCardSx}>
      <Stack spacing={2} sx={{ height: "100%" }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <SectionIconBox>
            <DirectionsCarFilledOutlinedIcon fontSize="small" />
          </SectionIconBox>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography sx={overviewEyebrowSx}>
                {L("Availability & participation", "Verfügbarkeit & Teilnahme")}
              </Typography>
              <Box component="button" type="button" onClick={() => setDetailsOpen(true)} sx={detailsLinkSx}>
                {L("Details", "Details")} →
              </Box>
            </Stack>
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              {L(
                "Emergency availability set against how many joined employees completed their assessment.",
                "Notfallverfügbarkeit im Verhältnis dazu, wie viele beigetretene Mitarbeitende ihr Assessment abgeschlossen haben."
              )}
            </Typography>
          </Box>
        </Stack>

        <Box>
          <Stack direction="row" spacing={0.75} alignItems="baseline">
            <Typography sx={{ fontWeight: 1000, color: availabilityColor, fontSize: 42, lineHeight: 1 }}>
              {availablePct}%
            </Typography>
            <Typography sx={{ color: "#6B7280", fontWeight: 900 }}>
              {L("high availability", "hoch verfügbar")}
            </Typography>
          </Stack>

          <SegmentedBar
            segments={[
              { key: "high", value: summary.high, color: "#16A34A" },
              { key: "medium", value: summary.medium, color: "#D6B866" },
              { key: "low", value: summary.low, color: "#DC2626" },
              { key: "unknown", value: unknownCount, color: "#E5E7EB" },
            ]}
          />
        </Box>

        <Stack spacing={1}>
          <AvailabilityLine label={L("High", "Hoch")} value={summary.high} total={total} color="#16A34A" />
          <AvailabilityLine label={L("Medium", "Mittel")} value={summary.medium} total={total} color="#D6B866" />
          <AvailabilityLine label={L("Low", "Niedrig")} value={summary.low} total={total} color="#DC2626" />
        </Stack>

        <Box sx={{ mt: "auto", pt: 1, borderTop: "1px solid #E5E7EB" }}>
          <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between">
            <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 800 }}>
              {L("Assessment completed", "Assessment abgeschlossen")}
            </Typography>
            <Typography variant="caption" sx={{ color: completionColor, fontWeight: 1000 }}>
              {completedAllCount}/{total} ({completionPct}%)
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={completionPct}
            sx={{
              mt: 0.6,
              height: 6,
              borderRadius: 20,
              bgcolor: "#E5E7EB",
              "& .MuiLinearProgress-bar": { bgcolor: completionColor, borderRadius: 20 },
            }}
          />
        </Box>
      </Stack>

      <AvailabilityDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        summary={summary}
        total={total}
        unknownCount={unknownCount}
        completedAllCount={completedAllCount}
        L={L}
      />
    </Paper>
  );
}

function SegmentedBar(props: { segments: { key: string; value: number; color: string }[] }) {
  const visible = props.segments.filter((segment) => segment.value > 0);

  return (
    <Box
      sx={{
        mt: 1.25,
        height: 10,
        borderRadius: 999,
        display: "flex",
        gap: "2px",
        overflow: "hidden",
        bgcolor: visible.length ? "#FFFFFF" : "#E5E7EB",
      }}
    >
      {visible.map((segment) => (
        <Box key={segment.key} sx={{ flex: `${segment.value} 1 0`, bgcolor: segment.color, borderRadius: 999 }} />
      ))}
    </Box>
  );
}

function AvailabilityDetailsDialog(props: {
  open: boolean;
  onClose: () => void;
  summary: AvailabilitySummary;
  total: number;
  unknownCount: number;
  completedAllCount: number;
  L: (en: string, de: string) => string;
}) {
  const { open, onClose, summary, total, unknownCount, completedAllCount, L } = props;
  const openCount = Math.max(0, total - completedAllCount);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ pb: 1.25 }}>
        <DialogHeader
          icon={<DirectionsCarFilledOutlinedIcon fontSize="small" />}
          title={L("Availability & participation details", "Details zu Verfügbarkeit & Teilnahme")}
          subtitle={L(
            "Aggregated availability levels, practical constraints and assessment participation.",
            "Aggregierte Verfügbarkeitsstufen, praktische Einschränkungen und Assessment-Teilnahme."
          )}
          closeLabel={L("Close", "Schließen")}
          onClose={onClose}
        />
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          <Box>
            {unknownCount > 0 ? (
              <AvailabilityDetailRow
                label={L("No availability signal", "Kein Verfügbarkeitssignal")}
                value={unknownCount}
                total={total}
                color="#9CA3AF"
              />
            ) : null}
          </Box>

          <Divider />

          <Box>
            <Typography sx={{ color: "#111827", fontWeight: 900, mb: 1 }}>
              {L("Assessment participation", "Assessment-Teilnahme")}
            </Typography>
            <AvailabilityDetailRow
              label={L("Joined employees", "Beigetretene Mitarbeitende")}
              value={total}
              total={total}
              color="#111827"
            />
            <AvailabilityDetailRow
              label={L("Assessment completed", "Assessment abgeschlossen")}
              value={completedAllCount}
              total={total}
              color="#16A34A"
            />
            <AvailabilityDetailRow
              label={L("Assessment open", "Assessment offen")}
              value={openCount}
              total={total}
              color="#D6B866"
            />
          </Box>

          <Divider />

          <Box>
            <Typography sx={{ color: "#111827", fontWeight: 900, mb: 1 }}>
              {L("Availability factors", "Verfügbarkeitsfaktoren")}
            </Typography>
            <Stack spacing={0.9}>
              <SimpleFactorRow
                label={L("Emergency volunteers", "Ehrenamt Einsatzorganisation")}
                value={summary.emergencyVolunteer}
                total={total}
              />
              <SimpleFactorRow
                label={L("Children or dependents", "Kinder oder Angehörige")}
                value={summary.childrenDependents}
                total={total}
              />
              <SimpleFactorRow
                label={L("Care dependents", "Pflegebedürftige Angehörige")}
                value={summary.careDependents}
                total={total}
              />
              <SimpleFactorRow label={L("No car access", "Kein Autozugang")} value={summary.noCarAccess} total={total} />
              <SimpleFactorRow
                label={L("Public transport reliant", "Auf ÖPNV angewiesen")}
                value={summary.publicTransportReliant}
                total={total}
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function PreparednessDetailsDialog(props: {
  open: boolean;
  onClose: () => void;
  summary: ScoreSummary;
  scoreBreakdown: ScoreBreakdown;
  flags: FlagSummary;
  total: number;
  L: (en: string, de: string) => string;
}) {
  const { open, onClose, summary, scoreBreakdown, flags, total, L } = props;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ pb: 1.25 }}>
        <DialogHeader
          icon={<TrendingUpOutlinedIcon fontSize="small" />}
          title={L("Readiness score breakdown", "Readiness-Score-Aufschlüsselung")}
          subtitle={L(
            "Company average with short explanations for score areas and flags.",
            "Unternehmensdurchschnitt mit kurzen Erklärungen zu Score-Bereichen und Flags."
          )}
          closeLabel={L("Close", "Schließen")}
          onClose={onClose}
        />
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          <Box>
            <ScoreDetailRow
              label={L("Overall preparedness", "Gesamtvorbereitung")}
              value={summary.avg}
              description={L(
                "Combined average across the available score areas.",
                "Kombinierter Durchschnitt aus den verfügbaren Score-Bereichen."
              )}
            />
            <ScoreDetailRow
              label={L("Personal Threat Level", "Persönliches Bedrohungsniveau")}
              value={scoreBreakdown.PBG.avg}
              description={L(
                "Threat exposure, role risk and personal vulnerability.",
                "Bedrohung, Rollenrisiko und persönliche Verwundbarkeit."
              )}
            />
            <ScoreDetailRow
              label={L("Practical Preparedness", "Praktische Vorsorge")}
              value={scoreBreakdown.Ext_PRS.avg}
              description={L(
                "Supplies, documents and household emergency readiness.",
                "Vorräte, Dokumente und Notfallvorsorge im Haushalt."
              )}
            />
            <ScoreDetailRow
              label={L("Engagement & Continuity", "Engagement & Kontinuität")}
              value={scoreBreakdown.E_Score.avg}
              description={L(
                "Cross-training, handover planning and emergency habits.",
                "Cross-Training, Übergabeplanung und Notfallroutinen."
              )}
            />
          </Box>

          <Divider />

          <Box>
            <Typography sx={{ color: "#111827", fontWeight: 900, mb: 1 }}>
              {L("Flag explanations", "Flag-Erklärungen")}
            </Typography>
            <Stack spacing={0.9}>
              <FlagExplanationRow
                icon={<WarningAmberOutlinedIcon fontSize="small" />}
                label={L("Single point of failure", "Single Point of Failure")}
                count={flags.spof}
                total={total}
                activeColor="#EA580C"
                text={L(
                  "One person holds a critical task with no trained backup.",
                  "Eine Person hält eine kritische Aufgabe ohne Vertretung."
                )}
              />
              <FlagExplanationRow
                icon={<MedicalServicesOutlinedIcon fontSize="small" />}
                label={L("Medical risk", "Medizinisches Risiko")}
                count={flags.medical}
                total={total}
                activeColor="#DC2626"
                text={L(
                  "Medical or mobility needs a crisis could interrupt.",
                  "Medizinische oder Mobilitätsbedarfe, die eine Krise unterbrechen könnte."
                )}
              />
              <FlagExplanationRow
                icon={<GroupsOutlinedIcon fontSize="small" />}
                label={L("Care responsibility", "Pflegeverantwortung")}
                count={flags.care}
                total={total}
                activeColor="#7C3AED"
                text={L(
                  "Dependents who rely on this person during a crisis.",
                  "Angehörige, die in einer Krise auf diese Person angewiesen wären."
                )}
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function DialogHeader(props: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  closeLabel: string;
  onClose: () => void;
}) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
        <SectionIconBox>{props.icon}</SectionIconBox>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 1000, color: "#111827", fontSize: 18 }}>
            {props.title}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280" }}>
            {props.subtitle}
          </Typography>
        </Box>
      </Stack>

      <IconButton aria-label={props.closeLabel} onClick={props.onClose}>
        <CloseRoundedIcon />
      </IconButton>
    </Stack>
  );
}

function AvailabilityDetailRow(props: { label: string; value: number; total: number; color: string }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ py: 0.85 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: props.color, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ color: "#374151", fontWeight: 900 }}>
          {props.label}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: "#111827", fontWeight: 1000 }}>
        {props.value}/{props.total}
      </Typography>
    </Stack>
  );
}

function SimpleFactorRow(props: { label: string; value: number; total: number }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
      <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 800 }}>
        {props.label}
      </Typography>
      <Typography variant="body2" sx={{ color: "#111827", fontWeight: 900 }}>
        {props.value}/{props.total}
      </Typography>
    </Stack>
  );
}

function ScoreDetailRow(props: { label: string; value: number | null; description: string }) {
  const value = props.value === null ? 0 : props.value;
  const color = props.value === null ? "#9CA3AF" : colorOnScale("E_Score", value);

  return (
    <Box sx={{ py: 0.8 }}>
      <Stack direction="row" spacing={2} alignItems="baseline" justifyContent="space-between">
        <Typography variant="body2" sx={{ color: "#374151", fontWeight: 900 }}>
          {props.label}
        </Typography>
        <Typography variant="body2" sx={{ color, fontWeight: 1000 }}>
          {formatPercent(props.value)}
        </Typography>
      </Stack>
      <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 400, mt: 0.2, display: "block" }}>
        {props.description}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          height: 8,
          borderRadius: 20,
          bgcolor: "#E5E7EB",
          mt: 0.7,
          "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 20 },
        }}
      />
    </Box>
  );
}

function FlagExplanationRow(props: {
  icon: React.ReactNode;
  label: string;
  count: number;
  total: number;
  text: string;
  activeColor: string;
}) {
  const color = props.count > 0 ? props.activeColor : "#15803D";

  return (
    <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 2, p: 1.2, bgcolor: "#F9FAFB" }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ color, display: "grid", placeItems: "center", mt: 0.2 }}>
          {props.icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="body2" sx={{ color: "#111827", fontWeight: 900 }}>
              {props.label}
            </Typography>
            <Chip size="small" label={`${props.count}/${props.total}`} sx={pillSx(color, `${color}12`, `${color}33`)} />
          </Stack>
          <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.3 }}>
            {props.text}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function ReadinessGauge(props: { value: number | null; color: string }) {
  const progress = props.value === null ? 0 : clampPercent(props.value);
  const angle = Math.PI - (progress / 100) * Math.PI;
  const needleLength = 68;
  const needleX = 110 + Math.cos(angle) * needleLength;
  const needleY = 104 - Math.sin(angle) * needleLength;
  const activeColor = props.value === null ? "#9CA3AF" : props.color;

  return (
    <Box
      sx={{
        mt: 1.25,
        width: "100%",
        maxWidth: 240,
        mx: "auto",
        display: "grid",
        placeItems: "center",
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 220 128"
        role="img"
        aria-label="Operational readiness gauge"
        sx={{ width: "100%", height: 128, display: "block" }}
      >
        <defs>
          <linearGradient id="readinessGaugeGradient" x1="24" y1="104" x2="196" y2="104" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#DC2626" />
            <stop offset="35%" stopColor="#EA580C" />
            <stop offset="60%" stopColor="#D6B866" />
            <stop offset="82%" stopColor="#84CC16" />
            <stop offset="100%" stopColor="#16A34A" />
          </linearGradient>
        </defs>
        <path
          d="M24 104 A86 86 0 0 1 196 104"
          fill="none"
          stroke="#E5E7EB"
          strokeWidth="18"
          strokeLinecap="round"
        />
        <path
          d="M24 104 A86 86 0 0 1 196 104"
          fill="none"
          stroke={props.value === null ? "#E5E7EB" : "url(#readinessGaugeGradient)"}
          strokeWidth="18"
          strokeLinecap="round"
        />
        <line
          x1="110"
          y1="104"
          x2={needleX}
          y2={needleY}
          stroke={activeColor}
          strokeWidth="5"
          strokeLinecap="round"
        />
        <circle cx="110" cy="104" r="9" fill="#FFFFFF" stroke={activeColor} strokeWidth="5" />
        <circle cx="110" cy="104" r="3" fill="#111827" />
      </Box>
    </Box>
  );
}

function CompanyModuleSections(props: {
  insights: ModuleInsight[];
  employeeCount: number;
  currentScore: number | null;
  currentScoreCount: number;
  maxPredictionGain: number | null;
  issueResolutionPotential: IssueResolutionPotential;
  lang: "en" | "de";
  L: (en: string, de: string) => string;
  showPrediction?: boolean;
}) {
  const {
    insights,
    employeeCount,
    currentScore,
    currentScoreCount,
    maxPredictionGain,
    issueResolutionPotential,
    lang,
    L,
    showPrediction = true,
  } = props;
  const topInsights = insights.slice(0, 4);

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        <Paper elevation={0} sx={sectionPanelSx}>
          <SectionHeader
            icon={<WarningAmberOutlinedIcon fontSize="small" />}
            title={L("Top company risks", "Wichtigste Unternehmensrisiken")}
            subtitle={L(
              "Modules most frequently recommended for employees in this company.",
              "Module, die Mitarbeitenden in diesem Unternehmen am häufigsten empfohlen werden."
            )}
          />

          {topInsights.length === 0 ? (
            <EmptyInsightState
              text={L(
                "No module recommendations are available yet.",
                "Noch keine Modulempfehlungen verfügbar."
              )}
            />
          ) : (
            <Stack spacing={1.25}>
              {topInsights.map((insight) => (
                <ModuleRiskItem
                  key={insight.moduleId}
                  insight={insight}
                  employeeCount={employeeCount}
                  lang={lang}
                  L={L}
                />
              ))}
            </Stack>
          )}
        </Paper>

        <Paper elevation={0} sx={sectionPanelSx}>
          <SectionHeader
            icon={<TrendingUpOutlinedIcon fontSize="small" />}
            title={L("Recommended actions", "Empfohlene Maßnahmen")}
            subtitle={L(
              "Priorities based on the same company-wide recommendation frequency.",
              "Prioritäten auf Basis derselben unternehmensweiten Empfehlungshäufigkeit."
            )}
          />

          {topInsights.length === 0 ? (
            <EmptyInsightState
              text={L(
                "Once employees receive module recommendations, actions will appear here.",
                "Sobald Mitarbeitende Modulempfehlungen erhalten, erscheinen hier Maßnahmen."
              )}
            />
          ) : (
            <Stack spacing={1.25}>
              {topInsights.map((insight, index) => (
                <ModuleActionItem
                  key={insight.moduleId}
                  insight={insight}
                  priority={index + 1}
                  lang={lang}
                  L={L}
                />
              ))}
            </Stack>
          )}
        </Paper>
      </Box>

      {showPrediction ? (
        <ReadinessPredictionSection
          currentScore={currentScore}
          currentScoreCount={currentScoreCount}
          maxPredictionGain={maxPredictionGain}
          issueResolutionPotential={issueResolutionPotential}
          insights={topInsights}
          L={L}
        />
      ) : null}
    </Stack>
  );
}

function ReadinessPredictionSection(props: {
  currentScore: number | null;
  currentScoreCount: number;
  maxPredictionGain: number | null;
  issueResolutionPotential: IssueResolutionPotential;
  insights: ModuleInsight[];
  L: (en: string, de: string) => string;
}) {
  const {
    currentScore,
    currentScoreCount,
    maxPredictionGain,
    issueResolutionPotential,
    insights,
    L,
  } = props;
  const hasMaxPrediction = maxPredictionGain !== null && Number.isFinite(maxPredictionGain);
  const maxProjectedGain = maxPredictionGain ?? 0;
  const maxProjectedScore = currentScore !== null ? clampPercent(currentScore + maxProjectedGain) : null;

  return (
    <Paper elevation={0} sx={sectionPanelSx}>
      <SectionHeader
        icon={<TrendingUpOutlinedIcon fontSize="small" />}
        title={L("Readiness prediction", "Readiness-Prognose")}
        subtitle={L(
          "Maximum estimated overall score from all recommended modules, with operational issue resolution shown separately.",
          "Maximal geschätzter Gesamtscore durch alle empfohlenen Module; operative Themenlösung wird separat gezeigt."
        )}
      />

      {currentScore === null || currentScoreCount === 0 || !hasMaxPrediction || insights.length === 0 ? (
        <EmptyInsightState
          text={L(
            "No projection is available until current scores and module impacts are available.",
            "Eine Prognose ist verfügbar, sobald aktuelle Scores und Moduleffekte vorliegen."
          )}
        />
      ) : (
        <PredictionColumnChart
          currentScore={currentScore}
          maxProjectedScore={maxProjectedScore}
          maxProjectedGain={maxProjectedGain}
          issueResolutionPotential={issueResolutionPotential}
          L={L}
        />
      )}
    </Paper>
  );
}

function PredictionColumnChart(props: {
  currentScore: number;
  maxProjectedScore: number | null;
  maxProjectedGain: number;
  issueResolutionPotential: IssueResolutionPotential;
  L: (en: string, de: string) => string;
}) {
  const {
    currentScore,
    maxProjectedScore,
    maxProjectedGain,
    issueResolutionPotential,
    L,
  } = props;
  const currentColor = colorOnScale("E_Score", currentScore);
  const maxProjectedValue = maxProjectedScore ?? 0;
  const maxProjectedColor = maxProjectedScore === null ? "#9CA3AF" : colorOnScale("E_Score", maxProjectedValue);
  const maxProjectedCaption =
    Math.abs(maxProjectedGain) > 0.05
      ? `${formatPercent(maxProjectedScore)} (${formatSignedImpact(maxProjectedGain)} ${L("pp", "PP")})`
      : formatPercent(maxProjectedScore);
  const issueResolutionTooltip = L(
    `Estimate after all recommended modules are completed, assuming SPoF backup coverage, medical/care support, and reduced car-access exposure where relevant. Current issue counts: ${issueResolutionPotential.spofCount} SPoF, ${issueResolutionPotential.medicalCount} medical, ${issueResolutionPotential.careCount} care, ${issueResolutionPotential.noCarCount} no car.`,
    `Schätzung nach Abschluss aller empfohlenen Module, mit Annahmen zu SPoF-Vertretung, medizinischer/Pflege-Unterstützung und reduziertem Autozugangsrisiko, wo relevant. Aktuelle Themen: ${issueResolutionPotential.spofCount} SPoF, ${issueResolutionPotential.medicalCount} medizinisch, ${issueResolutionPotential.careCount} Pflege, ${issueResolutionPotential.noCarCount} kein Auto.`
  );
  const ticks = [100, 75, 50, 25, 0];

  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: 2.5,
        bgcolor: "#F9FAFB",
        p: { xs: 1.5, sm: 2 },
      }}
    >
      <Box sx={{ display: "grid", gridTemplateRows: "280px auto", rowGap: 1.2 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "42px minmax(0, 1fr)", gap: 1.5, minHeight: 0 }}>
          <Box
            sx={{
              position: "relative",
              color: "#6B7280",
              fontSize: 12,
              fontWeight: 900,
            }}
          >
            {ticks.map((tick) => (
              <Box
                key={tick}
                sx={{
                  position: "absolute",
                  right: 0,
                  bottom: `${tick}%`,
                  transform: "translateY(50%)",
                }}
              >
                {tick}%
              </Box>
            ))}
          </Box>

          <Box sx={{ position: "relative", minWidth: 0, borderLeft: "1px solid #D1D5DB", borderBottom: "1px solid #D1D5DB" }}>
            {ticks.map((tick) => (
              <Box
                key={tick}
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: `${tick}%`,
                  borderTop: "1px solid #E5E7EB",
                }}
              />
            ))}

            <Box
              sx={{
                position: "absolute",
                inset: "8px 12px 0 12px",
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: { xs: 2, sm: 5 },
                alignItems: "end",
              }}
            >
              <PredictionColumn
                value={currentScore}
                caption={formatPercent(currentScore)}
                color={currentColor}
                tooltip={L(
                  "Current company average overall readiness.",
                  "Aktueller Unternehmensdurchschnitt der Gesamtbereitschaft."
                )}
              />
              <PredictionColumn
                value={maxProjectedValue}
                caption={maxProjectedCaption}
                color={maxProjectedColor}
                variant="estimate"
                tooltip={issueResolutionTooltip}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "42px minmax(0, 1fr)", gap: 1.5 }}>
          <Box />
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: { xs: 2, sm: 5 }, px: "12px" }}>
            <Typography variant="caption" sx={{ color: "#374151", fontWeight: 900, textAlign: "center" }}>
              {L("Current", "Aktuell")}
            </Typography>
            <Typography variant="caption" sx={{ color: "#374151", fontWeight: 900, textAlign: "center" }}>
              {L("Estimated", "Prognose")}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function PredictionColumn(props: {
  value: number;
  caption: string;
  detail?: string;
  color: string;
  tooltip: string;
  variant?: "actual" | "estimate";
}) {
  const normalizedHeight = clampPercent(props.value);
  const isEstimate = props.variant === "estimate";

  return (
    <Tooltip arrow title={props.tooltip}>
      <Box sx={{ position: "relative", height: "100%", minWidth: 0 }}>
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: `calc(${normalizedHeight}% + 8px)`,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: props.color,
              fontSize: { xs: 11, sm: 13 },
              fontWeight: 1000,
              lineHeight: 1.1,
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            }}
          >
            {props.caption}
          </Typography>
          {props.detail ? (
            <Typography
              sx={{
                color: "#6B7280",
                fontSize: { xs: 10, sm: 11 },
                fontWeight: 900,
                lineHeight: 1.1,
                mt: 0.25,
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              }}
            >
              {props.detail}
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            bottom: 0,
            transform: "translateX(-50%)",
            width: { xs: 54, sm: 72 },
            maxWidth: "72%",
            height: `${normalizedHeight}%`,
            minHeight: 4,
            borderRadius: "8px 8px 0 0",
            bgcolor: isEstimate ? `${props.color}18` : props.color,
            border: isEstimate ? `2px dashed ${props.color}` : "none",
            boxShadow: isEstimate ? "none" : "0 8px 18px rgba(17,24,39,0.12)",
          }}
        />
      </Box>
    </Tooltip>
  );
}

function SectionHeader(props: { icon: React.ReactNode; title: string; subtitle: string }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: 2 }}>
      <SectionIconBox>{props.icon}</SectionIconBox>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 900, color: "#111827" }}>{props.title}</Typography>
        <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.25 }}>
          {props.subtitle}
        </Typography>
      </Box>
    </Stack>
  );
}

function ModuleRiskItem(props: {
  insight: ModuleInsight;
  employeeCount: number;
  lang: "en" | "de";
  L: (en: string, de: string) => string;
}) {
  const { insight, employeeCount, lang, L } = props;
  const title = getLocalizedText(insight.titleEn, insight.titleDe, lang);
  const description = getLocalizedText(insight.descriptionEn, insight.descriptionDe, lang);
  const color = insight.employeePct >= 50 ? "#DC2626" : insight.employeePct >= 25 ? "#EA580C" : "#A16207";

  return (
    <Box sx={moduleListItemSx(color)}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: "#111827", fontWeight: 900 }}>
            M{insight.order}: {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.25 }}>
            {description ||
              L(
                "Recommended because related readiness gaps appeared repeatedly.",
                "Empfohlen, weil dazugehörige Bereitschaftslücken mehrfach aufgetreten sind."
              )}
          </Typography>
        </Box>

        <Tooltip
          arrow
          title={L(
            `${insight.employeeCount} of ${employeeCount} employees have this module recommended. That is ${insight.employeePct}% of the selected company.`,
            `${insight.employeeCount} von ${employeeCount} Mitarbeitenden haben dieses Modul empfohlen bekommen. Das sind ${insight.employeePct}% des ausgewählten Unternehmens.`
          )}
        >
          <Chip
            size="small"
            label={`${insight.employeeCount}/${employeeCount} ${L("employees", "Mitarb.")} · ${insight.employeePct}%`}
            sx={pillSx(color, `${color}12`, `${color}33`)}
          />
        </Tooltip>
      </Stack>

      {insight.topReasons.length > 0 ? (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {insight.topReasons.map((reason) => (
            <Chip
              key={reason.reason}
              size="small"
              label={`${reasonLabel(reason.reason, L)} ${reason.count}`}
              sx={pillSx("#374151", "#F9FAFB", "#E5E7EB")}
            />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

function ModuleActionItem(props: {
  insight: ModuleInsight;
  priority: number;
  lang: "en" | "de";
  L: (en: string, de: string) => string;
}) {
  const { insight, priority, lang, L } = props;
  const title = getLocalizedText(insight.titleEn, insight.titleDe, lang);
  const description = getLocalizedText(insight.descriptionEn, insight.descriptionDe, lang);
  const actionItems = insight.actionItems
    .map((item) => getLocalizedText(item.textEn, item.textDe, lang))
    .filter(Boolean);
  const impactPct = insight.impactPct;
  const hasPositiveImpact = impactPct !== null && Number.isFinite(impactPct) && impactPct > 0.05;
  const isCompletedByAllRecommended =
    insight.employeeCount > 0 && insight.impactCompletedCount >= insight.employeeCount;
  const impactFallback = isCompletedByAllRecommended
    ? {
        label: L("Completed by all", "Alle abgeschlossen"),
        color: "#15803D",
        bgcolor: "#F0FDF4",
        borderColor: "#BBF7D0",
        tooltip: L(
          "All employees with this recommendation have already completed this module.",
          "Alle Mitarbeitenden mit dieser Empfehlung haben dieses Modul bereits abgeschlossen."
        ),
      }
    : {
        label: L("Already reflected", "Bereits berücksichtigt"),
        color: "#374151",
        bgcolor: "#F9FAFB",
        borderColor: "#E5E7EB",
        tooltip: L(
          "This module is recommended, but the current scoring projection does not add a separate new score gain.",
          "Dieses Modul ist empfohlen, aber die aktuelle Score-Prognose weist keinen separaten neuen Score-Zuwachs aus."
        ),
      };

  return (
    <Box sx={actionListItemSx}>
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1.5,
            bgcolor: "#111827",
            color: "#FFFFFF",
            display: "grid",
            placeItems: "center",
            fontWeight: 1000,
            fontSize: 13,
            flexShrink: 0,
          }}
        >
          {priority}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ color: "#111827", fontWeight: 900 }}>
            {L("Prioritize", "Priorisieren")}: M{insight.order} {title}
          </Typography>

          {actionItems.length > 0 ? (
            <Stack spacing={0.45} sx={{ mt: 0.85 }}>
              {actionItems.map((item) => (
                <Box
                  key={item}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "8px minmax(0, 1fr)",
                    columnGap: 0.8,
                    alignItems: "start",
                  }}
                >
                  <Box
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      bgcolor: "#6B7280",
                      mt: "8px",
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" sx={{ color: "#374151", fontWeight: 500, lineHeight: 1.45 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.25 }}>
              {description ||
                L(
                  "Use this module as the next company action focus.",
                  "Nutze dieses Modul als nächsten Maßnahmenfokus."
                )}
            </Typography>
          )}

        </Box>

        <Stack spacing={0.5} alignItems="flex-end" sx={{ flexShrink: 0 }}>
          {hasPositiveImpact ? (
            <Tooltip
              arrow
              title={L(
                `Average projected gain per remaining recommended employee if the ${insight.impactCount} employees who have not completed this module yet complete it.`,
                `Durchschnittlich projizierter Zuwachs pro verbleibendem empfohlenen Mitarbeitenden, wenn die ${insight.impactCount} Mitarbeitenden, die dieses Modul noch nicht abgeschlossen haben, es abschließen.`
              )}
            >
              <Chip
                size="small"
                label={`${formatSignedImpact(Number(impactPct))} ${L("pp avg", "PP Ø")}`}
                sx={pillSx("#15803D", "#F0FDF4", "#BBF7D0")}
              />
            </Tooltip>
          ) : (
            <Tooltip arrow title={impactFallback.tooltip}>
              <Chip
                size="small"
                label={impactFallback.label}
                sx={pillSx(impactFallback.color, impactFallback.bgcolor, impactFallback.borderColor)}
              />
            </Tooltip>
          )}

          {!isCompletedByAllRecommended ? (
            <Tooltip
              arrow
              title={L(
                `${insight.impactCompletedCount} of ${insight.employeeCount} employees with this recommendation have already completed this module.`,
                `${insight.impactCompletedCount} von ${insight.employeeCount} Mitarbeitenden mit dieser Empfehlung haben dieses Modul bereits abgeschlossen.`
              )}
            >
              <Typography
                variant="caption"
                sx={{ color: "#6B7280", fontWeight: 800, lineHeight: 1.2, whiteSpace: "nowrap" }}
              >
                {insight.impactCompletedCount}/{insight.employeeCount} {L("completed", "abgeschlossen")}
              </Typography>
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
}

function formatSignedImpact(value: number) {
  const rounded = Number(value.toFixed(1));
  const prefix = rounded > 0 ? "+" : "";
  return `${prefix}${rounded.toFixed(1)}`;
}

function EmptyInsightState(props: { text: string }) {
  return (
    <Box
      sx={{
        border: "1px dashed #D1D5DB",
        borderRadius: 2.5,
        bgcolor: "#F9FAFB",
        p: 2,
      }}
    >
      <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 800 }}>
        {props.text}
      </Typography>
    </Box>
  );
}

function reasonLabel(reason: string, L: (en: string, de: string) => string) {
  const labels: Record<string, string> = {
    Q9: L("Document access", "Dokumentenzugang"),
    Q14: L("Physical limitation", "Körperliche Einschränkung"),
    Q15: L("Stress load", "Stressbelastung"),
    Q16: L("Mental overload", "Mentale Überlastung"),
    Q20: L("Shelter planning", "Schutzplanung"),
    Q21: L("Information quality", "Informationsqualität"),
    Q22: L("Community support", "Community-Unterstützung"),
    "Q24-Q27": L("Threat awareness", "Gefahrenbewusstsein"),
    Q31: L("Water supply", "Wasservorrat"),
    Q32: L("Food supply", "Lebensmittelvorrat"),
    Q33: L("Power backup", "Stromvorsorge"),
    Q34: L("Evacuation bag", "Notfallrucksack"),
    Q35: L("Documents", "Dokumente"),
    Q36: L("Mobility", "Mobilität"),
    Q38: L("First aid", "Erste Hilfe"),
    Q39: L("Medication", "Medikamente"),
    Q41: L("Hygiene", "Hygiene"),
    Q42: L("Cooking", "Kochen"),
    Q43: L("Cash and backups", "Bargeld und Backups"),
    Q44: L("Communication plan", "Kommunikationsplan"),
    Q45: L("Household plan", "Haushaltsplan"),
    "P37+Q35": L("Regional evacuation risk", "Regionales Evakuierungsrisiko"),
    "P37 shelter-related risk": L("Regional shelter risk", "Regionales Schutzrisiko"),
    "Medical flag": L("Medical flag", "Medizinische Flag"),
    "SPoF flag": L("SPoF flag", "SPoF-Flag"),
  };

  return labels[reason] ?? reason;
}

function AvailabilityLine(props: { label: string; value: number; total: number; color: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: props.color, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ color: "#374151", fontWeight: 800 }}>
          {props.label}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: "#111827", fontWeight: 900 }}>
        {props.value}/{props.total}
      </Typography>
    </Stack>
  );
}

function DepartmentBreakdown(props: {
  rows: DepartmentSummary[];
  employeeCount: number;
  L: (en: string, de: string) => string;
}) {
  const { rows, L } = props;

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <SectionIconBox>
            <ApartmentOutlinedIcon fontSize="small" />
          </SectionIconBox>

          <Box>
            <Typography sx={{ fontWeight: 900, color: "#111827" }}>
              {L("Department breakdown", "Abteilungsübersicht")}
            </Typography>

            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              {L(
                "Overall score, flags and operational availability grouped by department.",
                "Gesamtscore, Flags und operative Verfügbarkeit nach Abteilung."
              )}
            </Typography>
          </Box>
        </Stack>
      </Stack>

      <TableContainer sx={{ border: "1px solid #E5E7EB", borderRadius: 2.5 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={thSx}>{L("Department", "Abteilung")}</TableCell>

              <TableCell sx={thSx} align="right">
                {L("Employees", "Mitarbeitende")}
              </TableCell>

              <TableCell sx={thSx} align="right">
                {L("Availability", "Verfügbarkeit")}
              </TableCell>

              <TableCell sx={thSx} align="right">
                {L("Overall", "Gesamt")}
              </TableCell>

              <TableCell sx={thSx} align="right">
                {L("Flags", "Flags")}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" sx={{ color: "#6B7280" }}>
                    {L("No department data available yet.", "Noch keine Abteilungsdaten verfügbar.")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.department} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                      {row.department}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography sx={{ fontWeight: 800, color: "#374151" }}>
                      {row.employeeCount}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <AvailabilityCell availability={row.availability} L={L} />
                  </TableCell>

                  <TableCell align="right">
                    <PercentScoreCell summary={row.overall} />
                  </TableCell>

                  <TableCell align="right">
                    <FlagCell flags={row.flags} L={L} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

const overviewCardSx = {
  borderRadius: 3,
  border: "1px solid #E5E7EB",
  bgcolor: "#FFFFFF",
  p: 2,
  minHeight: 270,
} as const;

const sectionPanelSx = {
  borderRadius: 3,
  border: "1px solid #E5E7EB",
  bgcolor: "#FFFFFF",
  p: 2,
} as const;

const overviewEyebrowSx = {
  color: "#111827",
  fontWeight: 1000,
  fontSize: 13,
  textTransform: "uppercase",
} as const;

const detailsLinkSx = {
  appearance: "none",
  border: 0,
  bgcolor: "#EFF6FF",
  borderRadius: 999,
  color: "#2563EB",
  cursor: "pointer",
  fontSize: 14,
  fontWeight: 1000,
  lineHeight: 1,
  px: 1.15,
  py: 0.65,
  whiteSpace: "nowrap",
  boxShadow: "inset 0 0 0 1px rgba(37,99,235,0.2)",
  "&:hover": { bgcolor: "#DBEAFE", color: "#1D4ED8" },
  "&:focus-visible": {
    outline: "3px solid rgba(37,99,235,0.28)",
    outlineOffset: 3,
    borderRadius: 1,
  },
} as const;

const dialogPaperSx = {
  borderRadius: 3,
  border: "1px solid #E5E7EB",
} as const;

function moduleListItemSx(color: string) {
  return {
    borderRadius: 2.5,
    border: "1px solid #E5E7EB",
    borderLeft: `4px solid ${color}`,
    bgcolor: "#FFFFFF",
    p: 1.5,
  } as const;
}

const actionListItemSx = {
  borderRadius: 2.5,
  border: "1px solid #E5E7EB",
  bgcolor: "#F9FAFB",
  p: 1.5,
} as const;

function pillSx(color: string, bgcolor: string, borderColor: string) {
  return {
    height: 26,
    borderRadius: 999,
    bgcolor,
    color,
    border: `1px solid ${borderColor}`,
    fontWeight: 900,
  } as const;
}

function flagChipSx(color: string) {
  return {
    height: 26,
    borderRadius: 999,
    bgcolor: `${color}12`,
    color,
    border: `1px solid ${color}33`,
    fontWeight: 900,
    "& .MuiChip-icon": {
      color,
      fontSize: 16,
    },
  } as const;
}

function AdminCompanyList(props: {
  companies: CompanyRow[];
  employeeCounts: Record<string, number>;
  loading: boolean;
  error: string | null;
  onSelect: (companyId: string) => void;
  L: (en: string, de: string) => string;
}) {
  const { companies, employeeCounts, loading, error, onSelect, L } = props;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F3F4F6", p: { xs: 2, md: 3 } }}>
      <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <SectionIconBox>
              <AdminPanelSettingsOutlinedIcon />
            </SectionIconBox>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, color: "#111827", lineHeight: 1.1 }}>
                {L("Admin Dashboard", "Admin-Dashboard")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                {L(
                  "Select a company to view its aggregated readiness dashboard.",
                  "Wähle ein Unternehmen, um dessen aggregiertes Bereitschafts-Dashboard zu sehen."
                )}
              </Typography>
            </Box>
          </Stack>

          <Chip
            label={`${L("Companies", "Unternehmen")}: ${loading ? "…" : companies.length}`}
            variant="outlined"
            sx={{
              height: 42,
              borderRadius: 2.5,
              borderColor: "#D1D5DB",
              bgcolor: "#FFFFFF",
              color: "#111827",
              fontWeight: 900,
              px: 1,
              minWidth: 180,
              justifyContent: "center",
            }}
          />
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
            {error}
          </Alert>
        ) : null}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid #E5E7EB",
            bgcolor: "#FFFFFF",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
              <SectionIconBox>
                <ApartmentOutlinedIcon fontSize="small" />
              </SectionIconBox>
              <Box>
                <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                  {L("Companies", "Unternehmen")}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6B7280" }}>
                  {L(
                    "The employee count includes every profile currently assigned to the company.",
                    "Die Mitarbeiterzahl umfasst alle aktuell dem Unternehmen zugeordneten Profile."
                  )}
                </Typography>
              </Box>
            </Stack>

            {loading ? (
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={72} />
                <Skeleton variant="rounded" height={72} />
                <Skeleton variant="rounded" height={72} />
              </Stack>
            ) : companies.length === 0 ? (
              <Typography variant="body2" sx={{ color: "#6B7280", py: 2 }}>
                {L("No companies are available.", "Es sind keine Unternehmen verfügbar.")}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {companies.map((company) => (
                  <Box
                    component="button"
                    type="button"
                    key={company.id}
                    onClick={() => onSelect(company.id)}
                    sx={{
                      width: "100%",
                      border: "1px solid #E5E7EB",
                      borderRadius: 2.5,
                      bgcolor: "#FFFFFF",
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      textAlign: "left",
                      cursor: "pointer",
                      font: "inherit",
                      color: "inherit",
                      transition: "border-color 120ms ease, background-color 120ms ease",
                      "&:hover": {
                        bgcolor: "#F9FAFB",
                        borderColor: "#9CA3AF",
                      },
                      "&:focus-visible": {
                        outline: "3px solid #11182722",
                        borderColor: "#111827",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2.25,
                        bgcolor: "#F3F4F6",
                        display: "grid",
                        placeItems: "center",
                        color: "#374151",
                        flexShrink: 0,
                      }}
                    >
                      <ApartmentOutlinedIcon fontSize="small" />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900, color: "#111827" }} noWrap>
                        {company.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#6B7280" }}>
                        {employeeCounts[company.id] ?? 0} {L("employees", "Mitarbeitende")}
                      </Typography>
                    </Box>

                    <ChevronRightRoundedIcon sx={{ color: "#6B7280", flexShrink: 0 }} />
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

function EventList(props: {
  events: EventRow[];
  participantCounts: Record<string, number>;
  loading: boolean;
  error: string | null;
  onSelect: (eventId: string) => void;
  L: (en: string, de: string) => string;
}) {
  const { events, participantCounts, loading, error, onSelect, L } = props;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F3F4F6", p: { xs: 2, md: 3 } }}>
      <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <SectionIconBox>
              <AdminPanelSettingsOutlinedIcon />
            </SectionIconBox>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, color: "#111827", lineHeight: 1.1 }}>
                {L("Admin Dashboard", "Admin-Dashboard")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                {L(
                  "Select an event to view its aggregated readiness dashboard.",
                  "Wähle einen Anlass, um dessen aggregiertes Bereitschafts-Dashboard zu sehen."
                )}
              </Typography>
            </Box>
          </Stack>

          <Chip
            label={`${L("Events", "Anlässe")}: ${loading ? "…" : events.length}`}
            variant="outlined"
            sx={{
              height: 42,
              borderRadius: 2.5,
              borderColor: "#D1D5DB",
              bgcolor: "#FFFFFF",
              color: "#111827",
              fontWeight: 900,
              px: 1,
              minWidth: 180,
              justifyContent: "center",
            }}
          />
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
            {error}
          </Alert>
        ) : null}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid #E5E7EB",
            bgcolor: "#FFFFFF",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
              <SectionIconBox>
                <EventOutlinedIcon fontSize="small" />
              </SectionIconBox>
              <Box>
                <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                  {L("Events", "Anlässe")}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6B7280" }}>
                  {L(
                    "The participant count includes every employee currently assigned to the event.",
                    "Die Teilnehmerzahl umfasst alle aktuell dem Anlass zugeordneten Mitarbeitenden."
                  )}
                </Typography>
              </Box>
            </Stack>

            {loading ? (
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={72} />
                <Skeleton variant="rounded" height={72} />
              </Stack>
            ) : events.length === 0 ? (
              <Typography variant="body2" sx={{ color: "#6B7280", py: 2 }}>
                {L("No events are available.", "Es sind keine Anlässe verfügbar.")}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {events.map((event) => (
                  <Box
                    component="button"
                    type="button"
                    key={event.id}
                    onClick={() => onSelect(event.id)}
                    sx={{
                      width: "100%",
                      border: "1px solid #E5E7EB",
                      borderRadius: 2.5,
                      bgcolor: "#FFFFFF",
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      textAlign: "left",
                      cursor: "pointer",
                      font: "inherit",
                      color: "inherit",
                      transition: "border-color 120ms ease, background-color 120ms ease",
                      "&:hover": {
                        bgcolor: "#F9FAFB",
                        borderColor: "#9CA3AF",
                      },
                      "&:focus-visible": {
                        outline: "3px solid #11182722",
                        borderColor: "#111827",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2.25,
                        bgcolor: "#F3F4F6",
                        display: "grid",
                        placeItems: "center",
                        color: "#374151",
                        flexShrink: 0,
                      }}
                    >
                      <EventOutlinedIcon fontSize="small" />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900, color: "#111827" }} noWrap>
                        {event.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#6B7280" }}>
                        {(event.event_date ?? L("No date set", "Kein Datum")) +
                          " · " +
                          (participantCounts[event.id] ?? 0) +
                          " " +
                          L("participants", "Teilnehmende")}
                      </Typography>
                    </Box>

                    <ChevronRightRoundedIcon sx={{ color: "#6B7280", flexShrink: 0 }} />
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

function AdminEventDashboard(props: {
  event: EventRow;
  onBack: () => void;
  L: (en: string, de: string) => string;
  lang: "en" | "de";
}) {
  const { event, onBack, L, lang } = props;

  const [activeTab, setActiveTab] = React.useState<"overview" | "departments" | "plan" | "feedback">("overview");
  const [loading, setLoading] = React.useState(true);
  const [uiError, setUiError] = React.useState<string | null>(null);
  const [employeeCount, setEmployeeCount] = React.useState(0);
  const [eventEmployeeIds, setEventEmployeeIds] = React.useState<string[]>([]);
  const [overallScore, setOverallScore] = React.useState<ScoreSummary>({ avg: null, count: 0 });
  const [scoreBreakdown, setScoreBreakdown] = React.useState<ScoreBreakdown>(() => emptyScoreBreakdown());
  const [flagSummary, setFlagSummary] = React.useState<FlagSummary>(() => emptyFlagSummary());
  const [availabilitySummary, setAvailabilitySummary] = React.useState<AvailabilitySummary>(() =>
    emptyAvailabilitySummary()
  );
  const [completedAllCount, setCompletedAllCount] = React.useState(0);
  const [departmentRows, setDepartmentRows] = React.useState<DepartmentSummary[]>([]);
  const [moduleInsights, setModuleInsights] = React.useState<ModuleInsight[]>([]);
  const [moduleMaxPredictionGain, setModuleMaxPredictionGain] = React.useState<number | null>(null);
  const [issueResolutionPotential, setIssueResolutionPotential] = React.useState<IssueResolutionPotential>(() =>
    emptyIssueResolutionPotential()
  );

  React.useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setUiError(null);

      try {
        const { data: employeeRows, error: employeesErr } = await fetchEmployeesByOccasion(event.id);
        if (employeesErr) throw employeesErr;

        const eventEmployees = (employeeRows ?? []) as ManagerRow[];
        const employeeIds = eventEmployees.map((e) => e.id);

        if (!mounted) return;
        setEmployeeCount(employeeIds.length);
        setEventEmployeeIds(employeeIds);

        if (!employeeIds.length) {
          setOverallScore({ avg: null, count: 0 });
          setScoreBreakdown(emptyScoreBreakdown());
          setFlagSummary(emptyFlagSummary());
          setAvailabilitySummary(emptyAvailabilitySummary());
          setCompletedAllCount(0);
          setDepartmentRows([]);
          setModuleInsights([]);
          setModuleMaxPredictionGain(null);
          setIssueResolutionPotential(emptyIssueResolutionPotential());
          return;
        }

        const [
          { data: scoreRows, error: scoresErr },
          { data: flagRows, error: flagsErr },
          { data: availabilityRows, error: availabilityErr },
          { data: moduleRecommendationRows, error: moduleRecommendationErr },
          [
            { data: pbgQuestionRows, error: pbgQuestionErr },
            { data: extPrsQuestionRows, error: extPrsQuestionErr },
            { data: pbgAnswerRows, error: pbgAnswerErr },
            { data: extPrsAnswerRows, error: extPrsAnswerErr },
            { data: answerImprovementRows, error: answerImprovementErr },
            { data: moduleTaskCategoryRows, error: moduleTaskCategoryErr },
            { data: moduleTaskProgressRows, error: moduleTaskProgressErr },
          ],
        ] = await Promise.all([
          fetchEventKontrolScores(event.id, employeeIds),
          fetchEventAssessmentFlags(event.id, employeeIds),
          fetchManagerAvailabilityRows(employeeIds),
          fetchManagerModuleRecommendations(employeeIds),
          fetchManagerImpactInputs(employeeIds),
        ]);

        if (scoresErr) throw scoresErr;
        if (flagsErr) throw flagsErr;
        if (availabilityErr) throw availabilityErr;
        if (moduleRecommendationErr) throw moduleRecommendationErr;
        if (pbgQuestionErr) throw pbgQuestionErr;
        if (extPrsQuestionErr) throw extPrsQuestionErr;
        if (pbgAnswerErr) throw pbgAnswerErr;
        if (extPrsAnswerErr) throw extPrsAnswerErr;
        if (answerImprovementErr) throw answerImprovementErr;
        if (moduleTaskCategoryErr) throw moduleTaskCategoryErr;
        if (moduleTaskProgressErr) throw moduleTaskProgressErr;

        const scores = (scoreRows ?? []) as EmployeeScoreRow[];
        const flags = (flagRows ?? []) as AssessmentFlagRow[];
        const availability = (availabilityRows ?? []) as AvailabilityRow[];
        const moduleRecommendations = (moduleRecommendationRows ?? []) as unknown as ModuleRecommendationRow[];

        const byEmployee = new Map<string, Partial<Record<ScoreType, number>>>();
        for (const row of scores) {
          if (!row.employee_id || !row.score_type) continue;
          const numericScore =
            typeof row.kontrol_score === "number" ? row.kontrol_score : Number(row.kontrol_score);
          if (Number.isNaN(numericScore)) continue;
          const existing = byEmployee.get(row.employee_id) ?? {};
          existing[row.score_type] = numericScore;
          byEmployee.set(row.employee_id, existing);
        }

        const flagsByEmployee = new Map<string, AssessmentFlagRow>();
        for (const row of flags) {
          if (row.employee_id) flagsByEmployee.set(row.employee_id, row);
        }

        const availabilityByEmployee = new Map<string, AvailabilityRow>();
        for (const row of availability) {
          if (row.employee_id) availabilityByEmployee.set(row.employee_id, row);
        }

        const overallValues = employeeIds
          .map((id) => getOverallReadinessFromScores(byEmployee.get(id) ?? {}))
          .filter((value): value is number => value !== null);

        const pbgValues = scores
          .filter((r) => r.score_type === "PBG" && r.kontrol_score !== null)
          .map((r) => Number(r.kontrol_score))
          .filter((v) => !Number.isNaN(v));
        const extprsValues = scores
          .filter((r) => r.score_type === "Ext_PRS" && r.kontrol_score !== null)
          .map((r) => Number(r.kontrol_score))
          .filter((v) => !Number.isNaN(v));
        const eksValues = scores
          .filter((r) => r.score_type === "E_Score" && r.kontrol_score !== null)
          .map((r) => Number(r.kontrol_score))
          .filter((v) => !Number.isNaN(v));

        const nextFlagSummary: FlagSummary = employeeIds.reduce((summary, id) => {
          const row = flagsByEmployee.get(id);
          const availabilityRow = availabilityByEmployee.get(id);
          const hasSpof = row?.spof === true;
          const hasMedical = row?.medical === true;
          const hasCare = availabilityRow?.has_care_dependents === true;

          if (hasSpof) summary.spof += 1;
          if (hasMedical) summary.medical += 1;
          if (hasCare) summary.care += 1;
          if (hasSpof || hasMedical || hasCare) summary.any += 1;

          return summary;
        }, emptyFlagSummary());

        const nextAvailabilitySummary = summarizeAvailability(
          employeeIds.map((id) => availabilityByEmployee.get(id)).filter(Boolean) as AvailabilityRow[]
        );

        const nextCompletedAllCount = employeeIds.filter((id) => {
          const employeeScores = byEmployee.get(id);
          return Boolean(
            employeeScores?.PBG !== undefined &&
              employeeScores?.Ext_PRS !== undefined &&
              employeeScores?.E_Score !== undefined
          );
        }).length;

        const deptMap = new Map<string, ManagerRow[]>();
        for (const employee of eventEmployees) {
          const dept =
            employee.department && employee.department.trim()
              ? employee.department.trim()
              : L("Unassigned", "Nicht zugeordnet");

          const list = deptMap.get(dept) ?? [];
          list.push(employee);
          deptMap.set(dept, list);
        }

        const deptSummaries: DepartmentSummary[] = Array.from(deptMap.entries())
          .map(([department, deptEmployees]) => {
            const deptFlags: FlagSummary = deptEmployees.reduce((summary, employee) => {
              const row = flagsByEmployee.get(employee.id);
              const availabilityRow = availabilityByEmployee.get(employee.id);
              const hasSpof = row?.spof === true;
              const hasMedical = row?.medical === true;
              const hasCare = availabilityRow?.has_care_dependents === true;

              if (hasSpof) summary.spof += 1;
              if (hasMedical) summary.medical += 1;
              if (hasCare) summary.care += 1;
              if (hasSpof || hasMedical || hasCare) summary.any += 1;

              return summary;
            }, emptyFlagSummary());

            const deptAvailability = summarizeAvailability(
              deptEmployees
                .map((employee) => availabilityByEmployee.get(employee.id))
                .filter(Boolean) as AvailabilityRow[]
            );
            const deptOverall = deptEmployees
              .map((employee) => getOverallReadinessFromScores(byEmployee.get(employee.id) ?? {}))
              .filter((value): value is number => value !== null);

            return {
              department,
              employeeCount: deptEmployees.length,
              overall: { avg: avg(deptOverall), count: deptOverall.length },
              flags: deptFlags,
              availability: deptAvailability,
            };
          })
          .sort((a, b) => a.department.localeCompare(b.department));

        const moduleImpactInput = {
          recommendations: moduleRecommendations,
          scoresByEmployee: byEmployee,
          flagsByEmployee,
          pbgQuestions: (pbgQuestionRows ?? []) as PbgQuestionRow[],
          extPrsQuestions: (extPrsQuestionRows ?? []) as ExtPrsQuestionRow[],
          pbgAnswers: (pbgAnswerRows ?? []) as PbgAnswerRow[],
          extPrsAnswers: (extPrsAnswerRows ?? []) as ExtPrsAnswerRow[],
          answerImprovements: (answerImprovementRows ?? []) as AnswerImprovementRow[],
          moduleTaskCategories: (moduleTaskCategoryRows ?? []) as unknown as ModuleTaskCategoryRow[],
          moduleTaskProgress: (moduleTaskProgressRows ?? []) as ModuleTaskProgressRow[],
        };
        const moduleImpacts = calculateModuleImpactByModule(moduleImpactInput);
        const nextModuleInsights = summarizeModuleRecommendations(
          moduleRecommendations,
          eventEmployees.length,
          moduleImpacts
        );
        const nextMaxPredictionGain = calculateCombinedModuleImpact({
          ...moduleImpactInput,
          moduleIds: Array.from(new Set(moduleRecommendations.map((row) => row.module_id).filter(Boolean))),
        });
        const nextIssueResolutionPotential = summarizeIssueResolutionPotential(
          eventEmployees,
          flagsByEmployee,
          availabilityByEmployee
        );

        if (!mounted) return;

        setOverallScore({ avg: avg(overallValues), count: overallValues.length });
        setScoreBreakdown({
          PBG: normalizedScoreSummary(pbgValues, "PBG"),
          Ext_PRS: normalizedScoreSummary(extprsValues, "Ext_PRS"),
          E_Score: normalizedScoreSummary(eksValues, "E_Score"),
        });
        setFlagSummary(nextFlagSummary);
        setAvailabilitySummary(nextAvailabilitySummary);
        setCompletedAllCount(nextCompletedAllCount);
        setDepartmentRows(deptSummaries);
        setModuleInsights(nextModuleInsights);
        setModuleMaxPredictionGain(nextMaxPredictionGain);
        setIssueResolutionPotential(nextIssueResolutionPotential);
      } catch (e: any) {
        if (!mounted) return;
        setUiError(
          e?.message ??
            L("Failed to load event statistics.", "Anlass-Statistiken konnten nicht geladen werden.")
        );
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [event.id, L]);

  const completionPct = employeeCount > 0 ? Math.round((completedAllCount / employeeCount) * 100) : 0;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F3F4F6", p: { xs: 2, md: 3 } }}>
      <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto" }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={onBack}
            sx={{
              minHeight: 44,
              borderRadius: 2.5,
              borderColor: "#D1D5DB",
              color: "#111827",
              bgcolor: "#FFFFFF",
              textTransform: "none",
              fontWeight: 900,
            }}
          >
            {L("Events", "Anlässe")}
          </Button>

          <SectionIconBox>
            <EventOutlinedIcon />
          </SectionIconBox>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, color: "#111827", lineHeight: 1.1 }} noWrap>
              {event.name}
            </Typography>
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              {event.event_date ?? L("No date set", "Kein Datum")}
            </Typography>
          </Box>
        </Stack>

        {uiError ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
            {uiError}
          </Alert>
        ) : null}

        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #E5E7EB", bgcolor: "#FFFFFF", overflow: "hidden", mb: 2 }}>
          <Box sx={{ p: { xs: 1, sm: 1.25 }, display: "flex", justifyContent: "center" }}>
            <Tabs
              value={activeTab}
              onChange={(_e, value) => setActiveTab(value)}
              variant="fullWidth"
              sx={{
                width: "100%",
                minHeight: 44,
                bgcolor: "#F3F4F6",
                borderRadius: 2.5,
                p: 0.5,
                "& .MuiTabs-indicator": { display: "none" },
                "& .MuiTab-root": {
                  minHeight: 38,
                  borderRadius: 2,
                  color: "#6B7280",
                  fontWeight: 900,
                  textTransform: "none",
                },
                "& .Mui-selected": {
                  bgcolor: "#FFFFFF",
                  color: "#111827",
                  boxShadow: "0 1px 2px rgba(17,24,39,0.08)",
                },
              }}
            >
              <Tab value="overview" label={L("Overall view", "Gesamtansicht")} />
              <Tab value="departments" label={L("Department breakdown", "Abteilungsübersicht")} />
              <Tab value="plan" label={L("Recommended plan", "Empfohlener Plan")} />
              <Tab value="feedback" label={L("Team feedback", "Team-Feedback")} />
            </Tabs>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #E5E7EB", bgcolor: "#FFFFFF", p: { xs: 2, md: 2.5 } }}>
          {loading ? (
            <Stack spacing={2}>
              <Skeleton variant="rounded" height={activeTab === "departments" ? 320 : 190} />
            </Stack>
          ) : activeTab === "overview" ? (
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                  gap: 2,
                  alignItems: "stretch",
                }}
              >
                <OperationalReadinessCard
                  summary={overallScore}
                  scoreBreakdown={scoreBreakdown}
                  flags={flagSummary}
                  total={employeeCount}
                  L={L}
                />

                <AvailabilityParticipationCard
                  summary={availabilitySummary}
                  completionPct={completionPct}
                  completedAllCount={completedAllCount}
                  total={employeeCount}
                  L={L}
                />
              </Box>

              <ReadinessPredictionSection
                currentScore={overallScore.avg}
                currentScoreCount={overallScore.count}
                maxPredictionGain={moduleMaxPredictionGain}
                issueResolutionPotential={issueResolutionPotential}
                insights={moduleInsights.slice(0, 4)}
                L={L}
              />

              <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
                <Button
                  variant="contained"
                  endIcon={<ChevronRightRoundedIcon />}
                  onClick={() => setActiveTab("plan")}
                  sx={{
                    minHeight: 44,
                    borderRadius: 2.5,
                    bgcolor: "#111827",
                    color: "#FFFFFF",
                    textTransform: "none",
                    fontWeight: 900,
                    px: 2,
                    width: { xs: "100%", sm: "auto" },
                    "&:hover": { bgcolor: "#1F2937" },
                  }}
                >
                  {L("Check your plan here", "Plan hier ansehen")}
                </Button>
              </Box>
            </Stack>
          ) : activeTab === "departments" ? (
            <DepartmentBreakdown rows={departmentRows} employeeCount={employeeCount} L={L} />
          ) : activeTab === "plan" ? (
            <CompanyModuleSections
              insights={moduleInsights}
              employeeCount={employeeCount}
              currentScore={overallScore.avg}
              currentScoreCount={overallScore.count}
              maxPredictionGain={moduleMaxPredictionGain}
              issueResolutionPotential={issueResolutionPotential}
              lang={lang}
              L={L}
              showPrediction={false}
            />
          ) : (
            <FeedbackInsightsPanel employeeIds={eventEmployeeIds} L={L} />
          )}
        </Paper>
      </Box>
    </Box>
  );
}

function PercentScoreCell(props: { summary: ScoreSummary }) {
  const color = props.summary.avg === null ? "#9CA3AF" : colorOnScale("E_Score", props.summary.avg);

  return (
    <Typography sx={{ fontWeight: 900, color }}>
      {formatPercent(props.summary.avg)}
    </Typography>
  );
}

function FlagCell(props: { flags: FlagSummary; L: (en: string, de: string) => string }) {
  const { flags, L } = props;

  if (flags.any === 0) {
    return (
      <Typography variant="body2" sx={{ color: "#16A34A", fontWeight: 900 }}>
        {L("None", "Keine")}
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.75} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
      {flags.spof > 0 ? (
        <Chip
          size="small"
          label={`SPoF ${flags.spof}`}
          sx={{
            height: 24,
            borderRadius: 999,
            bgcolor: "#FFF7ED",
            color: "#C2410C",
            border: "1px solid #FED7AA",
            fontWeight: 900,
          }}
        />
      ) : null}

      {flags.medical > 0 ? (
        <Chip
          size="small"
          label={`${L("Medical", "Med.")} ${flags.medical}`}
          sx={{
            height: 24,
            borderRadius: 999,
            bgcolor: "#FEF2F2",
            color: "#B91C1C",
            border: "1px solid #FECACA",
            fontWeight: 900,
          }}
        />
      ) : null}

      {flags.care > 0 ? (
        <Chip
          size="small"
          label={`${L("Care", "Pflege")} ${flags.care}`}
          sx={{
            height: 24,
            borderRadius: 999,
            bgcolor: "#F5F3FF",
            color: "#6D28D9",
            border: "1px solid #DDD6FE",
            fontWeight: 900,
          }}
        />
      ) : null}
    </Stack>
  );
}


function AvailabilityCell(props: {
  availability: AvailabilitySummary;
  L: (en: string, de: string) => string;
}) {
  const { availability, L } = props;

  return (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
      {availability.low === 0 && availability.medium === 0 && availability.high === 0 ? (
        <Typography variant="body2" sx={{ color: "#9CA3AF", fontWeight: 900 }}>
          {L("No data", "Keine Daten")}
        </Typography>
      ) : null}
      {availability.low > 0 ? (
        <Chip
          size="small"
          label={`${L("Low", "Niedrig")} ${availability.low}`}
          sx={{ height: 24, borderRadius: 999, bgcolor: "#FEF2F2", color: "#B91C1C", border: "1px solid #FECACA", fontWeight: 900 }}
        />
      ) : null}
      {availability.medium > 0 ? (
        <Chip
          size="small"
          label={`${L("Med", "Mittel")} ${availability.medium}`}
          sx={{ height: 24, borderRadius: 999, bgcolor: "#FFFBEB", color: "#A16207", border: "1px solid #FDE68A", fontWeight: 900 }}
        />
      ) : null}
      {availability.high > 0 ? (
        <Chip
          size="small"
          label={`${L("High", "Hoch")} ${availability.high}`}
          sx={{ height: 24, borderRadius: 999, bgcolor: "#F0FDF4", color: "#15803D", border: "1px solid #BBF7D0", fontWeight: 900 }}
        />
      ) : null}
    </Stack>
  );
}

const thSx = {
  color: "#6B7280",
  fontWeight: 900,
  bgcolor: "#F9FAFB",
  borderBottom: "1px solid #E5E7EB",
} as const;
